import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { flushSync } from 'react-dom';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Plus,
  Save,
  Trash2,
  Copy,
  RotateCw,
  RotateCcw,
  Users,
  Grid3X3,
  ZoomIn,
  ZoomOut,
  Magnet,
  Palette,
  Ruler as RulerIcon,
  Share2,
  Download,
  Armchair,
  Pencil,
  Undo2,
  Redo2,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Hash,
  Search,
  X,
  Printer,
  Link2,
  Sparkles,
  List,
  Filter,
  Layers,
  Maximize2,
  Minimize2,
  Box,
  History,
  DraftingCompass,
  UserCheck,
} from 'lucide-react';
import { LibraryCategoryTabs } from '../components/floor-plan/LibraryCategoryTabs';
import { DecorLookModal } from '../components/floor-plan/look/DecorLookModal';
import { ObjectDecorControl } from '../components/decor-library/ObjectDecorControl';
import { dressableOf, itemDecorName, withItemDecor } from '../components/floor-plan/itemDecor';
import { useEventDecor } from '../hooks/useDecor';
import { LibraryItemList } from '../components/floor-plan/LibraryItemList';
import {
  FAVOURITES_CATEGORY,
  MOST_USED_CATEGORY,
  favouriteItems,
  isVirtualCategory,
  mostUsedItems,
  useLibraryPrefs,
} from '../components/floor-plan/libraryPrefs';
import type {
  FloorPlanObject,
  Guest,
  Room,
  TableGroup,
  FloorPlanObjectTypeConfig,
  SaveFloorPlanObjectsResult,
} from '@peles/shared';
import {
  AI_RENDER_ENABLED_SETTING,
  FLOOR_PLAN_CLEARANCE_SETTING,
  TABLE_GROUP_COLORS,
  clampClearance,
  calculateChairRowGroup,
  calculateTableFootprint,
  calculateDistanceFt,
  formatLength,
  isLengthUnits,
  measureRoomShape,
  orderPlansByPart,
  readDecorLook,
  resolveDecor,
  type DecorLook,
  type DecorPicks,
  roomShapeFromRoom,
  tableSectionOf,
  type ChairSides,
  type LengthUnits,
  type ResolvedDecorValues,
} from '@peles/shared';
import { CollapsibleSidePanel } from '../components/floor-plan/CollapsibleSidePanel';
import { SerpentineControls } from '../components/floor-plan/SerpentineControls';
import { SpacingControl } from '../components/floor-plan/SpacingControl';
import { LengthUnitsToggle } from '../components/floor-plan/LengthUnitsToggle';
import { GridSizeControl } from '../components/floor-plan/GridSizeControl';
import { ChairTypeSelect } from '../components/floor-plan/ChairTypeSelect';
import { SeatingSectionControl } from '../components/floor-plan/SeatingSectionControl';
import { PlanPartTabs } from '../components/floor-plan/PlanPartTabs';
import { RoomCapacitySummary } from '../components/floor-plan/RoomCapacitySummary';
import { PlanPartButton } from '../components/floor-plan/PlanPartButton';
import {
  PlanPartModal,
  type NewPartPlanValues,
  type PlanPartValues,
} from '../components/floor-plan/PlanPartModal';
import {
  describePlanPart,
  hasPlanParts,
  nextEveningPart,
  readEveningParts,
} from '../components/floor-plan/planParts';
import { ChairTypesPanel } from '../components/floor-plan/ChairTypesPanel';
import { SidePanelTabs, type SidePanelTab } from '../components/floor-plan/SidePanelTabs';
import { readChairTypes, takesChairType, chairTypeOf } from '../components/floor-plan/chairTypes';
import { useAppSettingLists } from '../hooks/useAppSettings';
import { CanvasThemeToggle } from '../components/floor-plan/CanvasThemeToggle';
import {
  CANVAS_PALETTES,
  CanvasPaletteContext,
  isCanvasThemeChoice,
  resolveCanvasTheme,
  type CanvasThemeChoice,
} from '../components/floor-plan/canvasPalette';
import { useTheme } from '../hooks/useTheme';
import { GridLines } from '../components/floor-plan/GridLines';
import { usePlanGridSize } from '../components/floor-plan/usePlanGridSize';
import { LengthInput } from '../components/common/LengthInput';
import { SpacingOverlay } from '../components/floor-plan/SpacingOverlay';
import {
  footprintIssues,
  newOverlaps,
  planFootprints,
  spacingItemName,
} from '../components/floor-plan/spacing';
import {
  ChairSeatPanel,
  SeatCapacityWarning,
  SeatCountField,
} from '../components/floor-plan/SeatControls';
import {
  useFloorPlans,
  useFloorPlan,
  useCreateFloorPlan,
  useDeleteFloorPlan,
  useAssignGuestsToTable,
  useCloneAsTemplate,
  useTableAssignments,
  useRooms,
  useShareFloorPlan,
  useAssignGuestToSeat,
  useTableMealCounts,
  useDuplicateFloorPlan,
  useUpdateFloorPlan,
} from '../hooks/useFloorPlans';
import { useQuery } from '@tanstack/react-query';
import { useGuests } from '../hooks/useGuests';
import { useLockSeating, useSeatingState, useSetPrimarySeating } from '../hooks/useSeating';
import { SeatingPlanControls } from '../components/floor-plan/SeatingPlanControls';
import { seatedAt, withPlanSeats } from '../components/floor-plan/planSeating';
import { useFloorPlanSeatingMode } from '../components/floor-plan/seating-mode/useFloorPlanSeatingMode';
import { SeatingModePanel } from '../components/floor-plan/seating-mode/SeatingModePanel';
import { SeatingCanvasLayer } from '../components/floor-plan/seating-mode/SeatingCanvasLayer';
import { SeatingDnd } from '../components/seating/SeatingDnd';
import { useAppSettingValue } from '../hooks/useAppSettingValue';
import { useSettingsFloorPlanObjectTypes } from '../hooks/useSettings';
import { useEvent, useEvents } from '../hooks/useEvents';
import { useVenues } from '../hooks/useVenues';
import { getAppLogoUrl } from '../api/events';
import {
  type ObjectLibraryItem,
  type CanvasItem,
  TABLE_DEFAULTS,
  TABLE_SHAPE_LABELS,
  TABLE_TYPE_REVERSE,
  TABLE_TYPE_VISUAL_SHAPES,
  TABLE_DEFAULT_COLOR,
  HEAD_TABLE_COLOR,
  isTableItem,
  PIXELS_PER_FOOT,
  ftToPx,
  pxToFt,
  rotatedAabbSize,
  anchorToVisualTopLeft,
  visualTopLeftToAnchor,
  visualAnchorFloor,
  snapToGrid,
  calcCeremonyPositions,
  fromFloorPlanObject,
  canParentChairs,
  tableSeatCapacity,
  tableChairSides,
  withChairSides,
  buildChairsForParent,
  replaceChairsForParents,
  removeItemsWithSeats,
  CHAIRS_REMOVED_BY_HAND,
  seatNumberOf,
  isHalfRoundItem,
  isSerpentineItem,
  applySerpentinePatch,
  type SerpentinePatch,
  moveItemsWithChairs,
  expandMoveSet,
  chairsOf,
  isChairOf,
  CHAIR_PARENT_FIELD,
  copyItemConfig,
  migrateLoadedItems,
  newChairGroupId,
  newUid,
  newUserGroupId,
  isChairGroupId,
  OBJECT_LIBRARY,
  CanvasItemShape,
  Ruler,
  AutoAssignSeatsModal,
  AiSeatingModal,
  AiSeatingButton,
  RenderStudioButton,
  RenderStudioModal,
  FloorPlanPrintModal,
  FloorPlan3DModal,
  getCanvasOrientation,
  printFloorPlan,
  formatPrintDate,
  type FloorPlanPrintOptions,
  useFloorPlanSave,
  adoptServerIds,
  SaveConflictBanner,
  FloorPlanHistoryModal,
  AutoSaveControl,
  saveStatusLabel,
  RoomShapeEditorModal,
  RoomShapeLayer,
  DuplicatePlanModal,
  SaveAsPlanModal,
  CeremonyChairsModal,
  BulkEditObjectsModal,
  bulkEditTypesOf,
  type BulkEditValues,
  type CeremonyChairsOptions,
} from '../components/floor-plan';
import { useDialog } from '../context/DialogContext';
import { extractApiErrorCode, extractApiErrorMessage } from '../utils/apiError';
import { getFloorPlan } from '../api/floor-plans';
import { useInEventWorkspace } from '../components/events/workspace/EventWorkspaceContext';

// 20 maximally-distinct colors spread across the hue wheel with varied
// lightness so no two entries look similar even at small chair sizes.
const FAMILY_COLOR_PALETTE = [
  '#e6194b', // red
  '#3cb44b', // green
  '#4363d8', // blue
  '#f58231', // orange
  '#911eb4', // purple
  '#42d4f4', // cyan
  '#f032e6', // magenta
  '#bfef45', // lime
  '#469990', // teal
  '#9a6324', // brown
  '#800000', // maroon
  '#ffe119', // yellow
  '#aaffc3', // mint
  '#000075', // navy
  '#dcbeff', // lavender
  '#ffd8b1', // peach
  '#808000', // olive
  '#fabed4', // light pink
  '#a9a9a9', // grey
  '#fffac8', // cream
];

// ─── Workspace layout preferences ───────────────────────────────
//
// The canvas is the point of this page, so the chrome around it (page header,
// object library, properties panel) is collapsible and the choice is
// remembered per browser. Focus mode takes the editor out of the app shell
// and gives it the whole viewport.

const UI_PREFS_KEY = 'floorPlan:workspace';

type WorkspacePrefs = {
  focusMode: boolean;
  libraryCollapsed: boolean;
  panelCollapsed: boolean;
  /** Draw spacing problems on the canvas. */
  showSpacing: boolean;
  /** Refuse a move that puts one thing on another. */
  preventOverlap: boolean;
  /** Show lengths in feet or in feet and inches; either can be typed. */
  lengthUnits: LengthUnits;
  /** Draw the canvas like the app, or always light or dark. */
  canvasTheme: CanvasThemeChoice;
};

type SidePanelId = 'library' | 'properties' | 'chairs';

// 'library' is the guest list, for historical reasons.
const SIDE_PANEL_TABS: readonly SidePanelTab<SidePanelId>[] = [
  { id: 'library', label: 'Guests' },
  { id: 'properties', label: 'Properties' },
  { id: 'chairs', label: 'Chairs', testId: 'chairs-panel-tab' },
];

const DEFAULT_WORKSPACE_PREFS: WorkspacePrefs = {
  focusMode: false,
  libraryCollapsed: false,
  panelCollapsed: false,
  showSpacing: false,
  preventOverlap: false,
  lengthUnits: 'ft',
  canvasTheme: 'app',
};

function readWorkspacePrefs(): WorkspacePrefs {
  try {
    const raw = window.localStorage.getItem(UI_PREFS_KEY);
    if (!raw) return DEFAULT_WORKSPACE_PREFS;
    const parsed = JSON.parse(raw) as Partial<WorkspacePrefs>;
    return {
      focusMode: !!parsed.focusMode,
      libraryCollapsed: !!parsed.libraryCollapsed,
      panelCollapsed: !!parsed.panelCollapsed,
      showSpacing: !!parsed.showSpacing,
      preventOverlap: !!parsed.preventOverlap,
      lengthUnits: isLengthUnits(parsed.lengthUnits) ? parsed.lengthUnits : 'ft',
      canvasTheme: isCanvasThemeChoice(parsed.canvasTheme) ? parsed.canvasTheme : 'app',
    };
  } catch {
    return DEFAULT_WORKSPACE_PREFS;
  }
}

// ─── Main Page ──────────────────────────────────────────────────

export function FloorPlanPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const inWorkspace = useInEventWorkspace();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlPlanId = searchParams.get('planId');
  const fromVenueId = searchParams.get('fromVenueId');

  // State
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(urlPlanId);
  const [items, setItems] = useState<CanvasItem[]>([]);
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);
  const [multiSelect, setMultiSelect] = useState<Set<number>>(new Set());
  const [rubberBand, setRubberBand] = useState<{
    x: number;
    y: number;
    w: number;
    h: number;
  } | null>(null);
  const [guestSearchTerm, setGuestSearchTerm] = useState('');
  const [zoom, setZoom] = useState(1);
  const [zoomInput, setZoomInput] = useState('100');
  const [showGrid, setShowGrid] = useState(true);
  const [snapEnabled, setSnapEnabled] = useState(true);
  const [libraryCategory, setLibraryCategory] = useState<string>('Tables');
  // The office's chair types (Settings → App Settings), for the Chairs tab
  // and each table's picker.
  const { data: settingLists = [], isLoading: settingListsLoading } = useAppSettingLists();
  const chairTypeOptions = useMemo(() => readChairTypes(settingLists), [settingLists]);
  const eveningParts = useMemo(() => readEveningParts(settingLists), [settingLists]);
  const [activePanel, setActivePanel] = useState<SidePanelId>('library');
  const [tableGroups, setTableGroups] = useState<Record<number, TableGroup>>({});
  const [selectedRoomId, setSelectedRoomId] = useState<string | null>(null);
  const [selectedVenueId, setSelectedVenueId] = useState<string | null>(null);
  const [linkedEventId, setLinkedEventId] = useState<string | null>(null);
  const [workspace, setWorkspace] = useState<WorkspacePrefs>(readWorkspacePrefs);
  const {
    focusMode,
    libraryCollapsed,
    panelCollapsed,
    showSpacing,
    preventOverlap,
    lengthUnits,
    canvasTheme,
  } = workspace;
  const { resolved: appTheme } = useTheme();
  // Printing copies the live canvas, and paper is white: while it is copied
  // the canvas is drawn light, whatever it shows on screen.
  const [printingCanvas, setPrintingCanvas] = useState(false);
  const canvasPalette =
    CANVAS_PALETTES[printingCanvas ? 'light' : resolveCanvasTheme(canvasTheme, appTheme)];
  const setWorkspacePref = useCallback((patchPrefs: Partial<WorkspacePrefs>) => {
    setWorkspace((prev) => {
      const next = { ...prev, ...patchPrefs };
      try {
        window.localStorage.setItem(UI_PREFS_KEY, JSON.stringify(next));
      } catch {
        // A blocked localStorage must never break the editor.
      }
      return next;
    });
  }, []);
  const [measureMode, setMeasureMode] = useState(false);
  const [measurePoints, setMeasurePoints] = useState<{ x: number; y: number }[]>([]);

  // B11 state
  const [showChairs, setShowChairs] = useState(false);
  const [showLabels, setShowLabels] = useState(true);
  const [showTableNumbers, setShowTableNumbers] = useState(true);
  const [showIcons, setShowIcons] = useState(true);
  const [showFamilyColors, setShowFamilyColors] = useState(false);
  const [libSearch, setLibSearch] = useState('');
  const [chairDepthFt, setChairDepthFt] = useState(1.5);
  const [cloneCount, setCloneCount] = useState(3);
  const [cloneDirection, setCloneDirection] = useState<'right' | 'below'>('right');
  const [cloneSpacingFt, setCloneSpacingFt] = useState(2);
  const [chairSides, setChairSides] = useState<string>('top-bottom');
  // propertyTab will be used when Chairs/Guests tabs are added to the properties panel

  // Undo/Redo
  const undoHistory = useRef<CanvasItem[][]>([]);
  const undoPtr = useRef(-1);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const itemsRef = useRef<CanvasItem[]>([]);
  const canvasWidthRef = useRef(1200);
  const canvasHeightRef = useRef(800);
  // Tracks which plan ID we've already loaded to prevent background refetches
  // from overwriting unsaved local changes.
  const loadedPlanIdRef = useRef<string | null>(null);

  // Ceremony chairs dialog
  const [showCeremonyDialog, setShowCeremonyDialog] = useState(false);

  const { confirm: confirmDialog, prompt: promptDialog } = useDialog();
  const [toast, setToast] = useState<{ msg: string; isError?: boolean } | null>(null);

  // Room management state
  const [showShapeEditor, setShowShapeEditor] = useState(false);

  // Duplicate modal state
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);

  // Save As modal state
  const [showSaveAsModal, setShowSaveAsModal] = useState(false);
  // The part-of-the-evening dialog: this plan's part, or a plan for another.
  const [partModal, setPartModal] = useState<'edit' | 'new' | null>(null);
  const [saveAsName, setSaveAsName] = useState('');
  const [saveAsEventId, setSaveAsEventId] = useState<string>('');

  // Auto-assign seats modal state
  const [showAutoAssignModal, setShowAutoAssignModal] = useState(false);
  const [showAiSeatingModal, setShowAiSeatingModal] = useState(false);
  const aiSeatingEnabled = useAppSettingValue('ai_seating_enabled').data === true;
  const [showRenderStudio, setShowRenderStudio] = useState(false);
  const aiRenderEnabled = useAppSettingValue(AI_RENDER_ENABLED_SETTING).data === true;

  // Floating object list panel
  const [showObjectList, setShowObjectList] = useState(false);
  const [objectListSearch, setObjectListSearch] = useState('');
  const [objectListTypeFilter, setObjectListTypeFilter] = useState<string[]>([]);
  const [showObjectListTypeMenu, setShowObjectListTypeMenu] = useState(false);
  const [objListPos, setObjListPos] = useState({ x: 8, y: 8 });
  const objListDragRef = useRef<{
    startMouseX: number;
    startMouseY: number;
    startPosX: number;
    startPosY: number;
  } | null>(null);

  // Print options modal
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [show3DModal, setShow3DModal] = useState(false);

  // Canvas rotation - display only, does not affect object coordinates
  const [canvasRotation, setCanvasRotation] = useState<0 | 90 | 180 | 270>(0);

  // Bulk edit modal
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);

  // Version history
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  // Queries
  const { data: floorPlans = [], isLoading: loadingPlans } = useFloorPlans({
    eventId,
  });
  // An event's plans in the order its evening runs — the ceremony before
  // dinner. Plans across every event stay as listed.
  const orderedPlans = useMemo(
    () =>
      eventId
        ? orderPlansByPart(
            floorPlans,
            eveningParts.map((p) => p.value),
          )
        : floorPlans,
    [eventId, floorPlans, eveningParts],
  );
  const { data: templates = [] } = useFloorPlans({ isTemplate: true });
  const { data: loadedPlan } = useFloorPlan(selectedPlanId ?? undefined);
  // The event's look — linen, chairs, flowers, tableware, walls, floor — with
  // this plan's changes, as the 3D view and the AI render dress the room.
  const lookEventId = loadedPlan?.eventId ?? null;
  const { data: eventDecor } = useEventDecor(lookEventId);
  const planDecor = useMemo(
    () => resolveDecor(eventDecor, loadedPlan?.decor).values,
    [eventDecor, loadedPlan?.decor],
  );
  // The library pieces chosen for the whole plan — what an object without its
  // own shows in its Decor panel.
  const planPicks = useMemo<DecorPicks>(
    () => ({ ...(eventDecor?.look.picks ?? {}), ...(loadedPlan?.decor?.picks ?? {}) }),
    [eventDecor, loadedPlan?.decor],
  );
  const [showLook, setShowLook] = useState(false);
  // The look as it stands in the panel, unsaved, shown in the 3D view over it.
  const [previewDecor, setPreviewDecor] = useState<ResolvedDecorValues | null>(null);
  const { data: eventGuests = [] } = useGuests(eventId ? { eventId } : undefined);
  // Who sits where on the plan on screen. A guest's own table number follows
  // the event's seating plan only, so a ceremony plan reads its own seats.
  const { data: seatingState } = useSeatingState(
    eventId && selectedPlanId ? eventId : undefined,
    selectedPlanId,
  );
  const guests = useMemo(
    () => withPlanSeats(eventGuests, seatingState, selectedPlanId),
    [eventGuests, seatingState, selectedPlanId],
  );
  const lockSeating = useLockSeating(eventId);
  const setPrimarySeating = useSetPrimarySeating(eventId);
  // Seating mode: the plan stands still and guests are seated on it — the
  // unseated list replaces the object library, tables and chairs take drops
  // and taps, and nothing on the canvas moves or opens its properties.
  const [seatingModeOn, setSeatingModeOn] = useState(false);
  const planSeating = seatingState?.plan.id === selectedPlanId ? seatingState : undefined;
  const seatingMode = useFloorPlanSeatingMode(eventId, planSeating, seatingModeOn && !!planSeating);
  const toggleSeatingMode = useCallback(() => {
    setSeatingModeOn((on) => !on);
    setSelectedIdx(null);
    setMultiSelect(new Set());
  }, []);
  const { data: assignments = {} } = useTableAssignments(eventId, selectedPlanId);
  const { data: rooms = [] } = useRooms(selectedVenueId ?? undefined);
  const { data: venues = [] } = useVenues();
  const { data: currentEvent } = useEvent(eventId ?? '');
  const { data: allEvents = [] } = useEvents();
  const { data: objectTypeConfigs = [] } = useSettingsFloorPlanObjectTypes();
  const { data: appLogoUrl } = useQuery({ queryKey: ['app-logo-url'], queryFn: getAppLogoUrl });

  // Build library from API data (fall back to hardcoded if API returns empty)
  const objectLibrary: ObjectLibraryItem[] = useMemo(() => {
    if (objectTypeConfigs.length === 0) return OBJECT_LIBRARY;
    return objectTypeConfigs
      .filter((t: FloorPlanObjectTypeConfig) => t.isActive)
      .map((t: FloorPlanObjectTypeConfig) => ({
        configId: t.id,
        objectType: t.objectType ?? t.name.toUpperCase().replace(/[^A-Z0-9]/g, '_'),
        name: t.name,
        category: t.category,
        widthFt: t.widthFt,
        heightFt: t.heightFt,
        seats: t.defaultSeats ?? undefined,
        color: t.defaultColor,
        icon: t.icon ?? undefined,
        shape: t.shape ?? undefined,
        strokeColor: t.strokeColor ?? undefined,
        canHaveChairs: t.canHaveChairs,
        chairSizeFt: t.chairSizeFt ?? undefined,
        isTable: t.isTable ?? false,
        isBackground: t.isBackground,
      }));
  }, [objectTypeConfigs]);

  // This person's starred pieces and the ones they use most, as tabs before
  // the library's own categories — each only once it has something in it.
  const libraryPrefs = useLibraryPrefs();
  const favouriteLibrary = useMemo(
    () => favouriteItems(objectLibrary, libraryPrefs.prefs),
    [objectLibrary, libraryPrefs.prefs],
  );
  const mostUsedLibrary = useMemo(
    () => mostUsedItems(objectLibrary, libraryPrefs.prefs),
    [objectLibrary, libraryPrefs.prefs],
  );
  const libraryCategories = useMemo(
    () => [
      ...(favouriteLibrary.length > 0 ? [FAVOURITES_CATEGORY] : []),
      ...(mostUsedLibrary.length > 0 ? [MOST_USED_CATEGORY] : []),
      ...new Set(objectLibrary.map((o) => o.category)),
    ],
    [objectLibrary, favouriteLibrary.length, mostUsedLibrary.length],
  );
  // Unstarring the last favourite takes its tab away: fall back to the
  // library's first own category rather than a tab that is not there.
  useEffect(() => {
    if (!libraryCategories.includes(libraryCategory)) {
      const own = libraryCategories.find((c) => !isVirtualCategory(c));
      if (own) setLibraryCategory(own);
    }
  }, [libraryCategories, libraryCategory]);
  const virtualCategory = isVirtualCategory(libraryCategory);
  const libraryItems = useMemo(() => {
    const inCategory =
      libraryCategory === FAVOURITES_CATEGORY
        ? favouriteLibrary
        : libraryCategory === MOST_USED_CATEGORY
          ? mostUsedLibrary
          : objectLibrary.filter((o) => o.category === libraryCategory);
    const term = libSearch.toLowerCase();
    return term ? inCategory.filter((o) => o.name.toLowerCase().includes(term)) : inCategory;
  }, [libraryCategory, favouriteLibrary, mostUsedLibrary, objectLibrary, libSearch]);

  // Mutations
  const createFloorPlan = useCreateFloorPlan();
  const deleteFloorPlan = useDeleteFloorPlan();
  const assignGuests = useAssignGuestsToTable();
  const cloneAsTemplate = useCloneAsTemplate();
  const shareFloorPlan = useShareFloorPlan();
  const assignGuestToSeat = useAssignGuestToSeat();
  const { data: mealCounts = {} } = useTableMealCounts(eventId);
  const duplicateFloorPlan = useDuplicateFloorPlan();
  const updateFloorPlanMutation = useUpdateFloorPlan();

  // Selected room
  const selectedRoom = useMemo(() => {
    if (!selectedRoomId) return null;
    return rooms.find((r: Room) => r.id === selectedRoomId) ?? null;
  }, [rooms, selectedRoomId]);

  // A room drawn in the shape editor: its walls, doors and fixed features.
  const roomShape = useMemo(
    () => (selectedRoom?.shape ? roomShapeFromRoom(selectedRoom) : null),
    [selectedRoom],
  );
  const roomMeasurements = useMemo(
    () => (roomShape ? measureRoomShape(roomShape) : null),
    [roomShape],
  );

  // Canvas dimensions from room or defaults
  const canvasWidthPx = selectedRoom ? ftToPx(selectedRoom.widthFt) : 1200;
  const canvasHeightPx = selectedRoom ? ftToPx(selectedRoom.heightFt) : 800;

  // Keep refs in sync with state/derived values (for use in callbacks without stale closures)
  itemsRef.current = items;
  canvasWidthRef.current = canvasWidthPx;
  canvasHeightRef.current = canvasHeightPx;

  // ─── Toast helper ─────────────────────────────────────────────
  const showToast = useCallback((msg: string, isError?: boolean) => {
    setToast({ msg, isError });
    setTimeout(() => setToast(null), 3000);
  }, []);

  // The plan's own grid: drawn on the canvas, snapped to by a drag and
  // stepped by the arrow keys while snap is on. Saved on the plan (never with
  // its objects), one write at a time; a canvas with no plan keeps it for the
  // plan it becomes.
  const {
    size: gridSizeFt,
    ready: gridReady,
    choose: handleGridSizeChange,
    beginRead: beginGridRead,
    adopt: adoptGridSize,
  } = usePlanGridSize({
    planId: selectedPlanId,
    plan: loadedPlan,
    save: (id, size) => updateFloorPlanMutation.mutateAsync({ id, updates: { gridSizeFt: size } }),
    onSaveFailed: (id, onScreen) => {
      const name = floorPlans.find((fp) => fp.id === id)?.name;
      showToast(
        onScreen || !name
          ? 'Could not save the grid size'
          : `Could not save the grid size of "${name}" — it is back to its saved size`,
        true,
      );
    },
  });

  // ─── Saving ───────────────────────────────────────────────────
  const floorPlanSave = useFloorPlanSave({
    planId: selectedPlanId,
    getItems: () => itemsRef.current,
    onSaved: (objects) => setItems((prev) => adoptServerIds(prev, objects)),
    onStatus: showToast,
  });
  const { markDirty, loaded: markLoaded, save } = floorPlanSave;

  // ─── Undo/Redo ────────────────────────────────────────────────
  const recordChange = useCallback(
    (newItems: CanvasItem[]) => {
      undoHistory.current = undoHistory.current.slice(0, undoPtr.current + 1);
      undoHistory.current.push(newItems.map((it) => ({ ...it })));
      if (undoHistory.current.length > 50) {
        undoHistory.current.shift();
      } else {
        undoPtr.current++;
      }
      setCanUndo(undoPtr.current > 0);
      setCanRedo(false);
      markDirty();
    },
    [markDirty],
  );

  const handleUndo = useCallback(() => {
    if (undoPtr.current <= 0) return;
    undoPtr.current--;
    setItems(undoHistory.current[undoPtr.current]);
    setCanUndo(undoPtr.current > 0);
    setCanRedo(true);
    markDirty();
  }, [markDirty]);

  const handleRedo = useCallback(() => {
    if (undoPtr.current >= undoHistory.current.length - 1) return;
    undoPtr.current++;
    setItems(undoHistory.current[undoPtr.current]);
    setCanUndo(true);
    setCanRedo(undoPtr.current < undoHistory.current.length - 1);
    markDirty();
  }, [markDirty]);

  /**
   * Put stored objects on the canvas: a plan opening, a restored version, or
   * the other side of a save conflict. Starts a fresh undo history whose
   * first entry is the loaded state, so the first edit can be undone.
   */
  const hydrateObjects = useCallback(
    (planId: string, objects: FloorPlanObject[], version: number) => {
      const allItems = objects.map((o: FloorPlanObject) => fromFloorPlanObject(o));
      // Give every item a stable uid, re-point legacy chair links onto it and
      // rebuild the chair groups so each table owns only its own chairs. A plan
      // that needed repairing opens dirty on purpose: the fix then reaches the
      // database on the next save instead of being re-derived every time — and
      // re-deriving it later can land differently, since an ambiguous legacy
      // link is resolved by how close the chair sits to each candidate parent.
      const migrated = migrateLoadedItems(allItems);
      setItems(migrated.items);
      undoHistory.current = [migrated.items.map((it) => ({ ...it }))];
      undoPtr.current = 0;
      setCanUndo(false);
      setCanRedo(false);
      markLoaded(planId, version, migrated.changed);
      setSelectedIdx(null);
      setMultiSelect(new Set());
    },
    [markLoaded],
  );

  // ─── In-app confirm/prompt helpers (the app-wide dialog) ─────────
  const openConfirm = useCallback(
    async (title: string, message: string, onConfirm: () => void, confirmLabel = 'Delete') => {
      if (await confirmDialog({ title, message, confirmLabel })) onConfirm();
    },
    [confirmDialog],
  );

  const openPrompt = useCallback(
    async (
      title: string,
      placeholder: string,
      defaultValue: string,
      onConfirm: (v: string) => void,
    ) => {
      const value = await promptDialog({ title, placeholder, defaultValue, confirmLabel: 'OK' });
      if (value !== null) onConfirm(value);
    },
    [promptDialog],
  );

  // Sync items when plan loads
  useEffect(() => {
    if (!loadedPlan) return;
    // Only re-initialize when the plan ID changes. Background refetches from
    // TanStack Query must NOT overwrite unsaved local edits (labels, table
    // numbers, positions, etc.) — only a genuine plan switch should reset state.
    if (loadedPlanIdRef.current === loadedPlan.id) return;
    loadedPlanIdRef.current = loadedPlan.id;
    hydrateObjects(loadedPlan.id, loadedPlan.objects || [], loadedPlan.version ?? 0);
  }, [loadedPlan, hydrateObjects]);

  // The room is the plan's: whatever the plan as fetched says, whenever that
  // changes — its own room or none, never one left over from the plan shown
  // before. A pick shows at once and is saved; the refetch that follows a
  // save brings the same room back, a failed save puts the stored one back.
  const loadedPlanId = loadedPlan?.id;
  const loadedPlanRoomId = loadedPlan?.roomId ?? null;
  const loadedPlanRoomRef = useRef({ planId: loadedPlanId, roomId: loadedPlanRoomId });
  loadedPlanRoomRef.current = { planId: loadedPlanId, roomId: loadedPlanRoomId };
  const selectedPlanIdRef = useRef(selectedPlanId);
  selectedPlanIdRef.current = selectedPlanId;
  // A room save on its way for the plan: a copy fetched before it landed
  // would put the old room back until the refetch after it, so it waits.
  const roomSaveRef = useRef<{ planId: string; roomId: string | null } | null>(null);
  useEffect(() => {
    if (!loadedPlanId || roomSaveRef.current?.planId === loadedPlanId) return;
    setSelectedRoomId(loadedPlanRoomId);
  }, [loadedPlanId, loadedPlanRoomId]);

  // Auto-select plan from URL param or first available — the evening's first
  // part. Which part comes first can depend on the Settings order of parts,
  // so an event's first plan waits for the settings lists; a failed read
  // does not hold it (the order then falls back to the plans' times).
  useEffect(() => {
    if (orderedPlans.length > 0 && !selectedPlanId) {
      if (urlPlanId && orderedPlans.some((fp) => fp.id === urlPlanId)) {
        setSelectedPlanId(urlPlanId);
      } else if (!(eventId && settingListsLoading)) {
        setSelectedPlanId(orderedPlans[0].id);
      }
    }
  }, [orderedPlans, selectedPlanId, urlPlanId, eventId, settingListsLoading]);

  // Init venue from event's venueId (when navigated from event page)
  useEffect(() => {
    if (currentEvent?.venueId && !selectedVenueId) {
      setSelectedVenueId(currentEvent.venueId);
    }
  }, [currentEvent, selectedVenueId]);

  // When plan loads, sync selectedVenueId from its room and linkedEventId from its eventId
  useEffect(() => {
    if (loadedPlan) {
      if (loadedPlan.room?.venueId && !selectedVenueId) {
        setSelectedVenueId(loadedPlan.room.venueId);
      }
      if (!eventId && loadedPlan.eventId) {
        setLinkedEventId(loadedPlan.eventId);
      }
    }
  }, [loadedPlan, eventId, selectedVenueId]);

  // Unassigned guests
  const unassignedGuests = useMemo(() => {
    return guests.filter((g: Guest) => g.tableNumber == null);
  }, [guests]);

  // Filtered unassigned guests
  const filteredUnassigned = useMemo(() => {
    if (!guestSearchTerm) return unassignedGuests;
    const term = guestSearchTerm.toLowerCase();
    return unassignedGuests.filter(
      (g: Guest) =>
        g.firstName.toLowerCase().includes(term) ||
        (g.lastName && g.lastName.toLowerCase().includes(term)),
    );
  }, [unassignedGuests, guestSearchTerm]);

  // Guests assigned per table number — summed as total persons (male + female + kids)
  const seatedPerTable = useMemo(() => {
    const counts: Record<number, number> = {};
    guests.forEach((g: Guest) => {
      if (g.tableNumber != null) {
        const persons = (g.maleCount ?? 0) + (g.femaleCount ?? 0) + (g.kidCount ?? 0);
        counts[g.tableNumber] = (counts[g.tableNumber] || 0) + persons;
      }
    });
    return counts;
  }, [guests]);

  // Derived: split items into tables vs non-tables for quick access
  const tableItems = useMemo(() => items.filter(isTableItem), [items]);
  const objectItems = useMemo(() => items.filter((it) => !isTableItem(it)), [items]);

  const totalSeats = useMemo(
    () => tableItems.reduce((sum, t) => sum + (t.seats || 0), 0),
    [tableItems],
  );

  const totalPersonsAssigned = useMemo(() => {
    return guests.reduce((sum, g: Guest) => {
      if (g.tableNumber == null) return sum;
      return sum + (g.maleCount ?? 0) + (g.femaleCount ?? 0) + (g.kidCount ?? 0);
    }, 0);
  }, [guests]);

  // Family group color palette and per-seat color maps (for chair coloring)
  const familyGroupColors = useMemo<Record<string, string>>(() => {
    const groups = [
      ...new Set(
        guests.filter((g: Guest) => g.familyGroup).map((g: Guest) => g.familyGroup as string),
      ),
    ].sort();
    return Object.fromEntries(
      groups.map((group, i) => [group, FAMILY_COLOR_PALETTE[i % FAMILY_COLOR_PALETTE.length]]),
    );
  }, [guests]);

  // Per-table seat colors: tableNumber → { chairPosition → color }
  // Each guest record may represent multiple people (maleCount + femaleCount +
  // kidCount). We color that many consecutive chair positions starting at the
  // guest's seatNumber so that every physical seat gets a family color.
  const tableSeatColors = useMemo<Record<number, Record<number, string>>>(() => {
    if (!showFamilyColors) return {};
    const result: Record<number, Record<number, string>> = {};

    const partySize = (g: Guest) =>
      Math.max(1, (g.maleCount ?? 0) + (g.femaleCount ?? 0) + (g.kidCount ?? 0));

    // First pass: guests with explicit seat numbers — occupy partySize seats.
    guests.forEach((g: Guest) => {
      if (g.tableNumber == null || !g.familyGroup || g.seatNumber == null) return;
      const color = familyGroupColors[g.familyGroup];
      if (!color) return;
      if (!result[g.tableNumber]) result[g.tableNumber] = {};
      const size = partySize(g);
      for (let s = g.seatNumber; s < g.seatNumber + size; s++) {
        result[g.tableNumber][s] = color;
      }
    });

    // Second pass: guests without seat numbers — fill partySize sequential
    // unused positions per guest.
    const byTable: Record<number, Guest[]> = {};
    guests.forEach((g: Guest) => {
      if (g.tableNumber == null || !g.familyGroup || g.seatNumber != null) return;
      if (!byTable[g.tableNumber]) byTable[g.tableNumber] = [];
      byTable[g.tableNumber].push(g);
    });
    Object.entries(byTable).forEach(([tableNumStr, tableGuests]) => {
      const tableNum = Number(tableNumStr);
      if (!result[tableNum]) result[tableNum] = {};
      let pos = 1;
      tableGuests.forEach((g) => {
        const color = familyGroupColors[g.familyGroup!];
        if (!color) return;
        let assigned = 0;
        const size = partySize(g);
        while (assigned < size) {
          while (result[tableNum][pos]) pos++;
          result[tableNum][pos] = color;
          pos++;
          assigned++;
        }
      });
    });

    return result;
  }, [guests, familyGroupColors, showFamilyColors]);

  // uid → item, for the few places that need to resolve a link target.
  const itemsByUid = useMemo(() => {
    const map = new Map<string, CanvasItem>();
    items.forEach((it) => map.set(it.uid, it));
    return map;
  }, [items]);

  // ─── Spacing ─────────────────────────────────────────────────
  // What overlaps, and what stands closer than the office's aisle. Worked out
  // from the items as they are, so the badge is always current; drawn on the
  // canvas only when asked for.
  const clearanceSetting = useAppSettingValue(FLOOR_PLAN_CLEARANCE_SETTING).data;
  const clearanceFt = clampClearance(
    typeof clearanceSetting === 'number' ? clearanceSetting : undefined,
  );
  const spacingOpts = useMemo(
    () => ({ chairSizeFt: chairDepthFt, chairSides: chairSides as ChairSides }),
    [chairDepthFt, chairSides],
  );
  const spacingFootprints = useMemo(() => planFootprints(items, spacingOpts), [items, spacingOpts]);
  const spacingIssues = useMemo(
    () => footprintIssues(spacingFootprints, clearanceFt),
    [spacingFootprints, clearanceFt],
  );
  const spacingNameOf = useCallback(
    (uid: string) => spacingItemName(itemsByUid.get(uid)),
    [itemsByUid],
  );
  const selectSpacingPair = useCallback((a: string, b: string) => {
    const idxs = [a, b]
      .map((uid) => itemsRef.current.findIndex((it) => it.uid === uid))
      .filter((i) => i >= 0);
    if (idxs.length === 0) return;
    setSelectedIdx(idxs[0]);
    setMultiSelect(new Set(idxs));
    setActivePanel('properties');
  }, []);

  const nextTableNumber = useMemo(() => {
    if (tableItems.length === 0) return 1;
    return Math.max(...tableItems.map((t) => t.tableNumber!)) + 1;
  }, [tableItems]);

  const selectedItem = selectedIdx !== null ? items[selectedIdx] : null;

  // Object list panel helpers
  const objectTypeOptions = useMemo(
    () => [...new Set(items.map((it) => it.objectType))].sort(),
    [items],
  );

  // Types available for bulk edit (exclude CHAIR since chairs are managed via parent tables)
  const filteredObjectList = useMemo(() => {
    return items
      .map((item, idx) => ({ item, idx }))
      .filter(({ item }) => {
        const label = item.label || (isTableItem(item) ? `Table ${item.tableNumber}` : '');
        const searchLower = objectListSearch.toLowerCase();
        const nameMatch =
          !objectListSearch ||
          label.toLowerCase().includes(searchLower) ||
          item.objectType.toLowerCase().includes(searchLower);
        const typeMatch =
          objectListTypeFilter.length === 0 || objectListTypeFilter.includes(item.objectType);
        return nameMatch && typeMatch;
      });
  }, [items, objectListSearch, objectListTypeFilter]);

  // Room utilization
  const roomUtilization = useMemo(() => {
    const roomArea = selectedRoom
      ? selectedRoom.widthFt * selectedRoom.heightFt
      : pxToFt(canvasWidthPx) * pxToFt(canvasHeightPx);

    const usedArea = items.reduce((sum, it) => sum + it.widthFt * it.heightFt, 0);
    const pct = roomArea > 0 ? Math.round((usedArea / roomArea) * 100) : 0;

    return { used: Math.round(usedArea), total: Math.round(roomArea), pct };
  }, [items, selectedRoom, canvasWidthPx, canvasHeightPx]);

  // ─── Canvas rotation computed values ──────────────────────────
  // Display-only rotation shared with the client-portal canvas (helpers.ts).
  const {
    viewW: svgViewW,
    viewH: svgViewH,
    groupTransform: rotGroupTransform,
  } = getCanvasOrientation(canvasWidthPx, canvasHeightPx, canvasRotation);
  // SVG display dimensions (after rotation, width and height swap for 90/270)
  const svgDisplayW = svgViewW * zoom;
  const svgDisplayH = svgViewH * zoom;

  // ─── Handlers ───────────────────────────────────────────────

  const handleAddObject = useCallback(
    (libraryItem: ObjectLibraryItem) => {
      // For chair row groups, auto-calc dimensions
      let widthFt = libraryItem.widthFt;
      let heightFt = libraryItem.heightFt;
      if (libraryItem.objectType === 'CHAIR_ROW' && libraryItem.config) {
        const rows = (libraryItem.config.rows as number) || 1;
        const seatsPerRow = (libraryItem.config.seatsPerRow as number) || 10;
        const chairSpacing = (libraryItem.config.chairSpacingFt as number) || 2;
        const rowSpacing = (libraryItem.config.rowSpacingFt as number) || 3;
        const group = calculateChairRowGroup(rows, seatsPerRow, chairSpacing, rowSpacing);
        widthFt = group.totalWidthFt;
        heightFt = group.totalHeightFt;
      }
      const isCustomTable = !!libraryItem.isTable;
      const nonTableCount = itemsRef.current.filter((it) => !isTableItem(it)).length;
      const itemCount = itemsRef.current.length;
      const newItem: CanvasItem = {
        objectType: libraryItem.objectType,
        label: libraryItem.name,
        x: Math.max(
          0,
          Math.min(ftToPx(2.5 + itemCount * 0.75), canvasWidthRef.current - ftToPx(widthFt)),
        ),
        y: Math.max(
          0,
          Math.min(ftToPx(2.5 + itemCount * 0.75), canvasHeightRef.current - ftToPx(heightFt)),
        ),
        widthFt,
        heightFt,
        rotation: 0,
        color: libraryItem.color,
        opacity: libraryItem.objectType === 'AREA' ? 0.3 : 1,
        seats: libraryItem.seats ?? 0,
        config: {
          ...libraryItem.config,
          ...(libraryItem.shape ? { shape: libraryItem.shape } : {}),
          ...(libraryItem.icon ? { icon: libraryItem.icon } : {}),
          ...(libraryItem.strokeColor ? { strokeColor: libraryItem.strokeColor } : {}),
          ...(libraryItem.canHaveChairs !== undefined || isCustomTable
            ? { canHaveChairs: libraryItem.canHaveChairs || isCustomTable }
            : {}),
          ...(libraryItem.chairSizeFt !== undefined
            ? { chairSizeFt: libraryItem.chairSizeFt }
            : {}),
          ...(libraryItem.isBackground ? { isBackground: true } : {}),
        },
        uid: newUid(),
        sortOrder: isCustomTable ? nextTableNumber : nonTableCount,
        ...(isCustomTable ? { tableNumber: nextTableNumber } : {}),
      };
      const newItems = [...itemsRef.current, newItem];
      setItems(newItems);
      setSelectedIdx(newItems.length - 1);
      setActivePanel('properties');
      recordChange(newItems);
    },
    [nextTableNumber, recordChange],
  );

  // Everything that moves together with the item at `idx`: the multi-select
  // (when the dragged item is part of it), whole groups of any member, and
  // the chairs attached to any moved table/object.
  const computeDragMoveSet = useCallback(
    (idx: number) => {
      const seed =
        multiSelect.size > 0 && multiSelect.has(idx) ? new Set([idx, ...multiSelect]) : [idx];
      return expandMoveSet(itemsRef.current, seed);
    },
    [multiSelect],
  );

  // Live drag preview: while dragging, all co-moving items are rendered at
  // their offset positions so the whole selection follows the cursor (the
  // dragged shape itself also updates its own transform directly).
  const [dragPreview, setDragPreview] = useState<{
    set: Set<number>;
    dx: number;
    dy: number;
  } | null>(null);
  const dragMoveSetRef = useRef<{ idx: number; set: Set<number> } | null>(null);
  const dragRafRef = useRef<number | null>(null);
  const dragPendingDeltaRef = useRef({ dx: 0, dy: 0 });

  // Cancel any queued preview frame if the page unmounts mid-drag
  useEffect(() => {
    return () => {
      if (dragRafRef.current != null) cancelAnimationFrame(dragRafRef.current);
    };
  }, []);

  const handleItemDragMove = useCallback(
    (idx: number, dx: number, dy: number) => {
      if (!dragMoveSetRef.current || dragMoveSetRef.current.idx !== idx) {
        dragMoveSetRef.current = { idx, set: computeDragMoveSet(idx) };
      }
      const { set } = dragMoveSetRef.current;
      // A lone item already follows the cursor via its own transform.
      if (set.size <= 1) return;
      dragPendingDeltaRef.current = { dx, dy };
      if (dragRafRef.current == null) {
        dragRafRef.current = requestAnimationFrame(() => {
          dragRafRef.current = null;
          if (!dragMoveSetRef.current) return;
          setDragPreview({ set: dragMoveSetRef.current.set, ...dragPendingDeltaRef.current });
        });
      }
    },
    [computeDragMoveSet],
  );

  // With the lock on, every way of moving something — a drag, a nudge, typed
  // X/Y, aligning a selection — asks this before it lands: a move that would
  // put one thing on another is refused and says which two would meet.
  const refuseOverlap = useCallback(
    (before: CanvasItem[], after: CanvasItem[]) => {
      if (!preventOverlap) return false;
      const clash = newOverlaps(before, after, spacingOpts)[0];
      if (!clash) return false;
      const byUid = new Map(after.map((it) => [it.uid, it]));
      showToast(
        `${spacingItemName(byUid.get(clash.a))} would overlap ${spacingItemName(byUid.get(clash.b))}, so it stayed put. Turn off "Don't let a move put one thing on another" under Spacing to place it there.`,
        true,
      );
      return true;
    },
    [preventOverlap, spacingOpts, showToast],
  );

  const handleItemDragEnd = useCallback(
    (idx: number, x: number, y: number) => {
      dragMoveSetRef.current = null;
      if (dragRafRef.current != null) {
        cancelAnimationFrame(dragRafRef.current);
        dragRafRef.current = null;
      }
      setDragPreview(null);

      const finalX = snapEnabled ? snapToGrid(x, ftToPx(gridSizeFt)) : Math.round(x);
      const finalY = snapEnabled ? snapToGrid(y, ftToPx(gridSizeFt)) : Math.round(y);
      const draggedItem = itemsRef.current[idx];
      const dx = finalX - draggedItem.x;
      const dy = finalY - draggedItem.y;
      if (dx === 0 && dy === 0) return;

      const moveSet = computeDragMoveSet(idx);
      const deltas = new Map<number, { dx: number; dy: number }>();
      moveSet.forEach((i) => deltas.set(i, { dx, dy }));
      const newItems = moveItemsWithChairs(itemsRef.current, deltas).map((it, i) =>
        i === idx ? { ...it, x: finalX, y: finalY } : it,
      );
      // Left where it was: the drag preview is cleared above, so the item
      // snaps back rather than landing on the other one.
      if (refuseOverlap(itemsRef.current, newItems)) return;
      setItems(newItems);
      recordChange(newItems);
    },
    [snapEnabled, gridSizeFt, computeDragMoveSet, recordChange, refuseOverlap],
  );

  // Stable, index-aware handlers for the memoised canvas shapes. The click
  // reads the primary selection from a ref so selecting one item does not
  // hand every other shape a new handler.
  const selectedIdxRef = useRef<number | null>(null);
  selectedIdxRef.current = selectedIdx;
  const handleShapeClick = useCallback((e: React.MouseEvent | undefined, idx: number) => {
    const primary = selectedIdxRef.current;
    if (e?.shiftKey) {
      // Shift+click: toggle in multi-select
      setMultiSelect((prev) => {
        const next = new Set(prev);
        if (next.has(idx)) next.delete(idx);
        else next.add(idx);
        // Always include primary
        if (primary !== null) next.add(primary);
        return next;
      });
      if (primary === null) setSelectedIdx(idx);
    } else {
      setSelectedIdx(idx);
      setMultiSelect(new Set());
    }
    setActivePanel('properties');
  }, []);
  const handleShapeDragEnd = useCallback(
    (x: number, y: number, idx: number) => handleItemDragEnd(idx, x, y),
    [handleItemDragEnd],
  );
  const handleShapeDragMove = useCallback(
    (dx: number, dy: number, idx: number) => handleItemDragMove(idx, dx, dy),
    [handleItemDragMove],
  );

  const handleDeleteItem = useCallback(async () => {
    if (selectedIdx === null && multiSelect.size === 0) return;
    // If multi-selected, delete all selected items
    const toDelete = new Set(multiSelect.size > 0 ? multiSelect : [selectedIdx!]);
    // Guests seated at a table being deleted lose their seats when the plan
    // is saved — say so first, by name of the table.
    const tables = [...toDelete]
      .map((i) => itemsRef.current[i])
      .filter((it): it is CanvasItem => !!it && isTableItem(it) && !!it.uid);
    const lost =
      seatingState?.plan.id === selectedPlanId
        ? seatedAt(
            seatingState,
            tables.map((t) => t.uid),
          )
        : { guestIds: [], seats: 0 };
    if (lost.seats > 0) {
      const names = tables.map((t) => t.label || `Table ${t.tableNumber}`);
      const what = names.length === 1 ? names[0] : `${names.length} tables`;
      const ok = await confirmDialog({
        title: `Delete ${what}?`,
        message: `Deleting ${what} unseats ${lost.seats} ${
          lost.seats === 1 ? 'guest' : 'guests'
        } when the plan is saved.`,
        confirmLabel: 'Delete',
        variant: 'danger',
      });
      if (!ok) return;
    }
    // Deleting a table takes its chairs with it; deleting a chair takes that
    // seat off its table.
    const newItems = removeItemsWithSeats(itemsRef.current, toDelete);
    setItems(newItems);
    setSelectedIdx(null);
    setMultiSelect(new Set());
    recordChange(newItems);
  }, [selectedIdx, multiSelect, recordChange, seatingState, selectedPlanId, confirmDialog]);

  const handleMakePrimarySeating = useCallback(() => {
    if (!selectedPlanId) return;
    setPrimarySeating.mutate(selectedPlanId, {
      onSuccess: () => showToast('The guest list now reads seats from this plan'),
      onError: (e) =>
        showToast(extractApiErrorMessage(e, 'Could not change the seating plan'), true),
    });
  }, [selectedPlanId, setPrimarySeating, showToast]);

  const handleToggleSeatingLock = useCallback(
    (locked: boolean) => {
      if (!selectedPlanId) return;
      lockSeating.mutate(
        { floorPlanId: selectedPlanId, locked },
        {
          onSuccess: () => showToast(locked ? 'Seating locked for printing' : 'Seating unlocked'),
          onError: (e) =>
            showToast(extractApiErrorMessage(e, 'Could not change the seating lock'), true),
        },
      );
    },
    [selectedPlanId, lockSeating, showToast],
  );

  const handleRotateItem = useCallback(() => {
    if (selectedIdx === null) return;
    const newItems = itemsRef.current.map((it, i) =>
      i === selectedIdx ? { ...it, rotation: (it.rotation + 45) % 360 } : it,
    );
    setItems(newItems);
    recordChange(newItems);
  }, [selectedIdx, recordChange]);

  // Applies a single-field update to an item. Choosing a color for a table
  // marks it as custom-colored so the canvas stops overriding it with the
  // occupancy (empty/partial/full) coloring.
  const applyItemField = useCallback((it: CanvasItem, field: keyof CanvasItem, value: unknown) => {
    const updated = { ...it, [field]: value } as CanvasItem;
    if (field === 'color' && isTableItem(it)) {
      updated.config = { ...it.config, useCustomColor: true };
    }
    return updated;
  }, []);

  // Locate an item in a rebuilt array (chair re-placement shifts indexes).
  const findItemIndex = useCallback((arr: CanvasItem[], target: CanvasItem) => {
    return arr.findIndex((it) => it.uid === target.uid);
  }, []);

  const handleUpdateItemField = useCallback(
    (field: keyof CanvasItem, value: unknown) => {
      if (selectedIdx === null) return;
      const current = itemsRef.current[selectedIdx];
      let newItems: CanvasItem[];

      if ((field === 'x' || field === 'y') && current.objectType !== 'CHAIR') {
        // Repositioning a table/object via its X/Y fields carries its chairs.
        const dx = field === 'x' ? (value as number) - current.x : 0;
        const dy = field === 'y' ? (value as number) - current.y : 0;
        newItems = moveItemsWithChairs(itemsRef.current, new Map([[selectedIdx, { dx, dy }]]));
        if (refuseOverlap(itemsRef.current, newItems)) return;
      } else if (field === 'tableNumber' && isTableItem(current)) {
        // Chairs follow their parent by uid, so a renumber needs no re-linking.
        // Guests are assigned by table number though, so a duplicate silently
        // splits one table's guest list across two tables — warn about it.
        const clash = itemsRef.current.some(
          (it, i) => i !== selectedIdx && isTableItem(it) && it.tableNumber === (value as number),
        );
        if (clash) showToast(`Table number ${value} is already used by another table`, true);
        newItems = itemsRef.current.map((it, i) =>
          i === selectedIdx ? applyItemField(it, field, value) : it,
        );
      } else {
        newItems = itemsRef.current.map((it, i) =>
          i === selectedIdx ? applyItemField(it, field, value) : it,
        );
        if (field === 'seats' && canParentChairs(current)) {
          // Re-place existing chairs to match the new seat count.
          newItems = replaceChairsForParents(newItems, [selectedIdx], {
            chairSides: chairSides as ChairSides,
            defaultChairSizeFt: chairDepthFt,
            // Its chairs were removed one by one: a seat added back is placed.
            placeIfMissing: current.config?.[CHAIRS_REMOVED_BY_HAND] === true,
          });
          const newIdx = findItemIndex(newItems, current);
          if (newIdx >= 0 && newIdx !== selectedIdx) setSelectedIdx(newIdx);
        }
      }
      setItems(newItems);
      recordChange(newItems);
    },
    [
      selectedIdx,
      applyItemField,
      recordChange,
      chairSides,
      chairDepthFt,
      findItemIndex,
      refuseOverlap,
      showToast,
    ],
  );

  // An object's own decor (`config.decor`) — for one, a selection, or every
  // object of a type. Saved with the plan like any other change.
  const handleSetItemsDecor = useCallback(
    (indices: number[], change: ((current: DecorLook) => DecorLook) | null) => {
      const wanted = new Set(indices);
      const newItems = itemsRef.current.map((it, i) =>
        wanted.has(i)
          ? withItemDecor(it, change ? change(readDecorLook(it.config?.decor ?? null)) : null)
          : it,
      );
      setItems(newItems);
      recordChange(newItems);
    },
    [recordChange],
  );

  // Bulk field update for all multi-selected items. Chairs are managed by
  // their parent table, so only color changes apply to them.
  const handleBulkUpdateField = useCallback(
    (field: keyof CanvasItem, value: unknown) => {
      if (multiSelect.size === 0) return;
      let newItems: CanvasItem[];

      if (field === 'x' || field === 'y') {
        // Align on the VISIBLE edge (rotation-aware): `value` is the target
        // top-left of the rotated bounding box in px. Each item converts it to
        // its own stored anchor so their visible edges line up, chairs follow.
        const target = value as number;
        const deltas = new Map<number, { dx: number; dy: number }>();
        multiSelect.forEach((i) => {
          const it = itemsRef.current[i];
          if (!it || it.objectType === 'CHAIR') return;
          const wpx = ftToPx(it.widthFt);
          const hpx = ftToPx(it.heightFt);
          const visual = anchorToVisualTopLeft(it.x, it.y, wpx, hpx, it.rotation);
          const anchor = visualTopLeftToAnchor(
            field === 'x' ? target : visual.x,
            field === 'y' ? target : visual.y,
            wpx,
            hpx,
            it.rotation,
          );
          deltas.set(i, {
            dx: field === 'x' ? anchor.x - it.x : 0,
            dy: field === 'y' ? anchor.y - it.y : 0,
          });
        });
        newItems = moveItemsWithChairs(itemsRef.current, deltas);
        if (refuseOverlap(itemsRef.current, newItems)) return;
      } else {
        newItems = itemsRef.current.map((it, i) => {
          if (!multiSelect.has(i)) return it;
          if (it.objectType === 'CHAIR' && field !== 'color') return it;
          return applyItemField(it, field, value);
        });
        if (field === 'seats') {
          const parentIdxs = [...multiSelect].filter((i) => {
            const it = itemsRef.current[i];
            return !!it && canParentChairs(it);
          });
          const parentObjs = parentIdxs.map((i) => newItems[i]);
          newItems = replaceChairsForParents(newItems, parentIdxs, {
            chairSides: chairSides as ChairSides,
            defaultChairSizeFt: chairDepthFt,
          });
          // Chair indexes shifted — re-point the selection at the parents.
          const newSelection = new Set<number>();
          parentObjs.forEach((p) => {
            const idx = findItemIndex(newItems, p);
            if (idx >= 0) newSelection.add(idx);
          });
          if (newSelection.size > 0) {
            setMultiSelect(newSelection);
            if (selectedIdx === null || !newSelection.has(selectedIdx)) {
              setSelectedIdx([...newSelection][0]);
            }
          }
        }
      }
      setItems(newItems);
      recordChange(newItems);
    },
    [
      multiSelect,
      selectedIdx,
      applyItemField,
      recordChange,
      chairSides,
      chairDepthFt,
      findItemIndex,
      refuseOverlap,
    ],
  );

  // Save an explicit list of items immediately (used by bulk edit so the save
  // doesn't have to wait for auto-save or a manual button click).
  const saveItems = useCallback(
    (itemsToSave: CanvasItem[]) => {
      void floorPlanSave.save('QUIET', { items: itemsToSave });
    },
    [floorPlanSave],
  );

  // All canvas items — built-in tables, custom tables and background art —
  // save through the one objects endpoint, as a diff against what is stored.
  const handleSave = useCallback(() => {
    void floorPlanSave.save('MANUAL');
  }, [floorPlanSave]);

  /** A restored version replaces the canvas; the restore is already saved. */
  const handleRestored = useCallback(
    (result: SaveFloorPlanObjectsResult) => {
      if (!selectedPlanId) return;
      hydrateObjects(selectedPlanId, result.objects, result.version);
      setShowHistoryModal(false);
      showToast('Version restored');
    },
    [selectedPlanId, hydrateObjects, showToast],
  );

  /** Conflict: drop this canvas and load what the other editor saved. */
  const handleLoadTheirs = useCallback(async () => {
    if (!selectedPlanId) return;
    const gridRead = beginGridRead(selectedPlanId);
    const plan = await getFloorPlan(selectedPlanId);
    hydrateObjects(plan.id, plan.objects || [], plan.version ?? 0);
    adoptGridSize(plan.id, plan.gridSizeFt, gridRead);
    showToast('Loaded the latest saved version');
  }, [selectedPlanId, hydrateObjects, beginGridRead, adoptGridSize, showToast]);

  /** A new, empty plan of the room on screen, which becomes the plan shown. */
  const createEmptyPlan = useCallback(
    (name: string, part?: PlanPartValues, onCreated?: () => void) => {
      createFloorPlan.mutate(
        {
          eventId,
          name,
          width: canvasWidthPx,
          height: canvasHeightPx,
          gridSizeFt,
          roomId: selectedRoomId ?? undefined,
          ...part,
        },
        {
          onSuccess: (plan) => {
            setSelectedPlanId(plan.id);
            onCreated?.();
          },
          onError: () => showToast('The plan could not be created', true),
        },
      );
    },
    [
      eventId,
      createFloorPlan,
      canvasWidthPx,
      canvasHeightPx,
      gridSizeFt,
      selectedRoomId,
      showToast,
    ],
  );

  const handleCreatePlan = useCallback(() => {
    openPrompt('New Floor Plan', 'Floor plan name', 'Main Hall', (name) => {
      if (!name.trim()) return;
      createEmptyPlan(name.trim());
    });
  }, [createEmptyPlan, openPrompt]);

  // ─── Save As ────────────────────────────────────────────────
  // Saves the current canvas into a NEW floor plan record. Works both when
  // editing an existing plan (duplicate with a new name/event) and when
  // designing without any plan record — the in-memory work is preserved.
  const handleOpenSaveAs = useCallback(() => {
    if (selectedPlanId && loadedPlan) {
      setSaveAsName(`Copy of ${loadedPlan.name}`);
      setSaveAsEventId(loadedPlan.eventId ?? eventId ?? '');
    } else {
      setSaveAsName('New Floor Plan');
      setSaveAsEventId(eventId ?? linkedEventId ?? '');
    }
    setShowSaveAsModal(true);
  }, [selectedPlanId, loadedPlan, eventId, linkedEventId]);

  const handleSaveAs = useCallback(
    (saveAsName: string, saveAsEventId: string, part?: PlanPartValues) => {
      if (!saveAsName.trim() || createFloorPlan.isPending) return;
      createFloorPlan.mutate(
        {
          eventId: saveAsEventId || undefined,
          name: saveAsName.trim(),
          width: canvasWidthPx,
          height: canvasHeightPx,
          gridSizeFt,
          roomId: selectedRoomId ?? undefined,
          ...part,
        },
        {
          onSuccess: (plan) => {
            // The canvas now belongs to the new plan, unsaved until the copy
            // lands. The copy goes through the save queue like any save, so
            // an edit made while it is on the wire keeps the plan dirty, and
            // an autosave cannot race it. The source plan's object ids mean
            // nothing to the new plan: the server matches none and creates
            // every object afresh, and their ids are adopted by uid.
            markLoaded(plan.id, plan.version ?? 0, true);
            void save('MANUAL', { planId: plan.id, items: itemsRef.current }).then((ok) =>
              showToast(
                ok ? `Saved as "${plan.name}"` : 'Save As failed — objects not copied',
                !ok,
              ),
            );
            // Switch to the new plan but keep the in-memory items — mark the
            // plan as already loaded so the hydrate effect doesn't overwrite
            // the canvas with a stale (possibly empty) server copy.
            loadedPlanIdRef.current = plan.id;
            setSelectedPlanId(plan.id);
            setShowSaveAsModal(false);
            setPartModal(null);
          },
          onError: () => {
            showToast('Save As failed — plan not created', true);
          },
        },
      );
    },
    [createFloorPlan, save, canvasWidthPx, canvasHeightPx, selectedRoomId, showToast, markLoaded],
  );

  // ─── Parts of the evening ──────────────────────────────────

  /** Set which part of the evening the plan on screen lays out, and when. */
  const handleSavePlanPart = useCallback(
    (values: PlanPartValues) => {
      if (!selectedPlanId) return;
      updateFloorPlanMutation.mutate(
        { id: selectedPlanId, updates: values },
        {
          onSuccess: () => setPartModal(null),
          onError: () => showToast('The part of the evening could not be saved', true),
        },
      );
    },
    [selectedPlanId, updateFloorPlanMutation, showToast],
  );

  /**
   * A plan for another part of the evening, in the same event and room: a
   * copy of the canvas as it stands (through Save As, so unsaved edits come
   * too) to rearrange, or the empty room.
   */
  const handleNewPartPlan = useCallback(
    ({ name, copyCurrent, ...part }: NewPartPlanValues) => {
      if (copyCurrent) {
        // The canvas must hold the plan on screen, not the one before it.
        if (!selectedPlanId || loadedPlanIdRef.current !== selectedPlanId) {
          showToast('This plan is still loading — try again in a moment', true);
          return;
        }
        handleSaveAs(name, loadedPlan?.eventId ?? eventId ?? '', part);
      } else {
        createEmptyPlan(name, part, () => setPartModal(null));
      }
    },
    [handleSaveAs, createEmptyPlan, loadedPlan, eventId, selectedPlanId, showToast],
  );

  const suggestedPart = useMemo(
    () => nextEveningPart(orderedPlans, eveningParts),
    [orderedPlans, eveningParts],
  );

  const handleDeletePlan = useCallback(() => {
    if (!selectedPlanId) return;
    openConfirm('Delete Floor Plan', 'Delete this floor plan? This cannot be undone.', () => {
      deleteFloorPlan.mutate(selectedPlanId, {
        onSuccess: () => {
          setSelectedPlanId(null);
          setItems([]);
        },
      });
    });
  }, [selectedPlanId, deleteFloorPlan, openConfirm]);

  const handleSaveAsTemplate = useCallback(() => {
    if (!selectedPlanId) return;
    openPrompt('Save as Template', 'Template name', '', (name) => {
      if (!name.trim()) return;
      cloneAsTemplate.mutate({ id: selectedPlanId, templateName: name.trim() });
    });
  }, [selectedPlanId, cloneAsTemplate, openPrompt]);

  /**
   * A seating the server refused: a full table asks whether to squeeze them in
   * anyway (and does, on yes); anything else — a taken seat, a table that is
   * not on the plan — is said in the toast.
   */
  const handleSeatingRefusal = useCallback(
    async (err: unknown, seatAnyway: () => void) => {
      const message = extractApiErrorMessage(err, 'Could not seat the guest');
      if (extractApiErrorCode(err) !== 'OVER_CAPACITY') {
        showToast(message, true);
        return;
      }
      const ok = await confirmDialog({
        title: 'Table is full',
        message: `${message}. Seat them anyway?`,
        confirmLabel: 'Seat anyway',
        variant: 'warning',
      });
      if (ok) seatAnyway();
    },
    [confirmDialog, showToast],
  );

  const handleAssignGuest = useCallback(
    (guestId: string) => {
      if (!selectedItem || !isTableItem(selectedItem)) return;
      const input = {
        guestIds: [guestId],
        tableNumber: selectedItem.tableNumber!,
        ...(selectedPlanId ? { floorPlanId: selectedPlanId } : {}),
      };
      assignGuests.mutate(input, {
        onError: (err) =>
          handleSeatingRefusal(err, () =>
            assignGuests.mutate(
              { ...input, allowOverCapacity: true },
              {
                onError: (e) =>
                  showToast(extractApiErrorMessage(e, 'Could not seat the guest'), true),
              },
            ),
          ),
      });
    },
    [selectedItem, selectedPlanId, assignGuests, handleSeatingRefusal, showToast],
  );

  const handleSetTableGroup = useCallback(
    (group: TableGroup | '') => {
      if (selectedIdx === null || !selectedItem || !isTableItem(selectedItem)) return;
      const tn = selectedItem.tableNumber!;
      if (group === '') {
        setTableGroups((prev) => {
          const copy = { ...prev };
          delete copy[tn];
          return copy;
        });
      } else {
        setTableGroups((prev) => ({ ...prev, [tn]: group }));
      }
    },
    [selectedIdx, selectedItem],
  );

  // Convert SVG viewBox coordinates (visual/rotated space) back to logical canvas coordinates
  const toLogicalCoords = useCallback(
    (svgX: number, svgY: number): { x: number; y: number } => {
      switch (canvasRotation) {
        case 90:
          return { x: svgY, y: canvasHeightPx - svgX };
        case 180:
          return { x: canvasWidthPx - svgX, y: canvasHeightPx - svgY };
        case 270:
          return { x: canvasWidthPx - svgY, y: svgX };
        default:
          return { x: svgX, y: svgY };
      }
    },
    [canvasRotation, canvasWidthPx, canvasHeightPx],
  );

  // A rubber-band drag ends with a browser click event on the SVG, which
  // would immediately clear the selection it just made — suppress that click.
  const suppressCanvasClickRef = useRef(false);

  const handleCanvasClick = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      if (suppressCanvasClickRef.current) {
        suppressCanvasClickRef.current = false;
        return;
      }
      if (measureMode) {
        const svg = e.currentTarget;
        const rect = svg.getBoundingClientRect();
        const svgX = (e.clientX - rect.left) / zoom;
        const svgY = (e.clientY - rect.top) / zoom;
        const { x, y } = toLogicalCoords(svgX, svgY);
        setMeasurePoints((prev) => {
          if (prev.length >= 2) return [{ x, y }];
          return [...prev, { x, y }];
        });
        return;
      }
      setSelectedIdx(null);
      setMultiSelect(new Set());
    },
    [measureMode, seatingModeOn, zoom, toLogicalCoords],
  );

  // Rubber-band area selection
  const handleCanvasMouseDown = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      if (measureMode || seatingModeOn) return;
      // Start rubber-band if clicking empty canvas (not on a draggable item)
      // Items have cursor:grab style or a data-testid with canvas-table/canvas-object
      const target = e.target as SVGElement;
      const isOnItem =
        target.closest('[style*="cursor: grab"]') || target.closest('[data-testid^="canvas-"]');
      if (!isOnItem) {
        const svg = e.currentTarget;
        const rect = svg.getBoundingClientRect();
        const svgX = (e.clientX - rect.left) / zoom;
        const svgY = (e.clientY - rect.top) / zoom;
        const { x, y } = toLogicalCoords(svgX, svgY);
        setRubberBand({ x, y, w: 0, h: 0 });
      }
    },
    [measureMode, zoom, toLogicalCoords],
  );

  const handleCanvasMouseMove = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      if (!rubberBand) return;
      const svg = e.currentTarget;
      const rect = svg.getBoundingClientRect();
      const svgX = (e.clientX - rect.left) / zoom;
      const svgY = (e.clientY - rect.top) / zoom;
      const { x, y } = toLogicalCoords(svgX, svgY);
      setRubberBand((rb) => (rb ? { ...rb, w: x - rb.x, h: y - rb.y } : null));
    },
    [rubberBand, zoom, toLogicalCoords],
  );

  const handleCanvasMouseUp = useCallback(() => {
    if (!rubberBand || (Math.abs(rubberBand.w) < 5 && Math.abs(rubberBand.h) < 5)) {
      setRubberBand(null);
      return;
    }
    // Normalize rubber-band rectangle
    const rx = rubberBand.w >= 0 ? rubberBand.x : rubberBand.x + rubberBand.w;
    const ry = rubberBand.h >= 0 ? rubberBand.y : rubberBand.y + rubberBand.h;
    const rw = Math.abs(rubberBand.w);
    const rh = Math.abs(rubberBand.h);

    // Find items that overlap the rubber-band
    const selected = new Set<number>();
    items.forEach((item, idx) => {
      const iw = ftToPx(item.widthFt);
      const ih = ftToPx(item.heightFt);
      if (item.x + iw > rx && item.x < rx + rw && item.y + ih > ry && item.y < ry + rh) {
        selected.add(idx);
      }
    });

    if (selected.size > 0) {
      setMultiSelect(selected);
      setSelectedIdx([...selected][0]);
      setActivePanel('properties');
      suppressCanvasClickRef.current = true;
    }
    setRubberBand(null);
  }, [rubberBand, items]);

  // Group / Ungroup handlers
  const handleGroupSelected = useCallback(() => {
    if (multiSelect.size < 2) return;
    const gid = newUserGroupId();
    const newItems = itemsRef.current.map((it, i) =>
      multiSelect.has(i) ? { ...it, groupId: gid } : it,
    );
    setItems(newItems);
    recordChange(newItems);
    showToast(`Grouped ${multiSelect.size} items`);
  }, [multiSelect, recordChange, showToast]);

  const handleUngroupSelected = useCallback(() => {
    if (selectedIdx === null) return;
    const src = itemsRef.current[selectedIdx];
    if (!src.groupId) return;
    const gid = src.groupId;
    const count = itemsRef.current.filter((it) => it.groupId === gid).length;
    const newItems = itemsRef.current.map((it) =>
      it.groupId === gid ? { ...it, groupId: undefined } : it,
    );
    setItems(newItems);
    setMultiSelect(new Set());
    recordChange(newItems);
    showToast(`Ungrouped ${count} items`);
  }, [selectedIdx, recordChange, showToast]);

  const handlePlaceChairs = useCallback(() => {
    if (selectedIdx === null || !selectedItem) return;
    const src = selectedItem;
    if (!canParentChairs(src)) return;

    // Chairs belong to exactly one parent (matched on its uid), so removing
    // them here can never reach another table's chairs.
    const existingChairs = chairsOf(items, src);
    if (existingChairs.length > 0) {
      // Remove chairs; clear the parent's auto-created groupId
      const newItems = items
        .filter((it) => !isChairOf(it, src))
        .map((it) =>
          it.uid === src.uid && isChairGroupId(it.groupId) ? { ...it, groupId: undefined } : it,
        );
      // Update selectedIdx to the parent's new position after filter
      const newParentIdx = newItems.findIndex((it) => it.uid === src.uid);
      setItems(newItems);
      setSelectedIdx(newParentIdx >= 0 ? newParentIdx : null);
      recordChange(newItems);
      return;
    }

    // Auto-group: assign same groupId to parent + all new chairs
    const gid = src.groupId || newChairGroupId();

    // Also assign groupId to the parent item if it doesn't already have one,
    // and have it remember the sides its chairs are placed on.
    const sides = tableChairSides(src, chairSides as ChairSides);
    const updatedItems = items.map((it, i) =>
      i === selectedIdx ? withChairSides(it.groupId ? it : { ...it, groupId: gid }, sides) : it,
    );
    const newChairs = buildChairsForParent(src, {
      chairSizeFt: chairDepthFt,
      chairSides: sides,
      sortBase: items.length,
      groupId: gid,
    });
    const newItems = [...updatedItems, ...newChairs];
    setItems(newItems);
    recordChange(newItems);
  }, [selectedIdx, selectedItem, items, recordChange, chairDepthFt, chairSides]);

  // Replace existing chairs when depth or sides change
  const replaceChairsForSelected = useCallback(
    (newDepth: number, newSides?: ChairSides) => {
      if (selectedIdx === null || !selectedItem) return;
      if (!canParentChairs(selectedItem)) return;
      const newItems = replaceChairsForParents(items, [selectedIdx], {
        chairSides: chairSides as ChairSides,
        // A size change keeps the table's own sides; a sides change sets them.
        sides: newSides,
        defaultChairSizeFt: newDepth,
        chairSizeFt: newDepth,
      });
      if (newItems === items) {
        // No chairs to lay out again; a table that chose its sides still keeps them.
        if (!newSides) return;
        const chosen = items.map((it, i) =>
          i === selectedIdx ? withChairSides(it, newSides) : it,
        );
        if (chosen[selectedIdx] === items[selectedIdx]) return;
        setItems(chosen);
        recordChange(chosen);
        return;
      }
      const newIdx = findItemIndex(newItems, selectedItem);
      if (newIdx >= 0 && newIdx !== selectedIdx) setSelectedIdx(newIdx);
      setItems(newItems);
      recordChange(newItems);
    },
    [selectedIdx, selectedItem, items, recordChange, findItemIndex, chairSides],
  );

  // A serpentine is sized by its radius, depth and arc rather than a free
  // width × height: the box follows from them, and any chairs already placed
  // are re-seated along the new curve.
  const handleUpdateSerpentine = useCallback(
    (patch: SerpentinePatch) => {
      if (selectedIdx === null) return;
      const current = itemsRef.current[selectedIdx];
      if (!current || !isSerpentineItem(current)) return;
      let newItems = itemsRef.current.map((it, i) =>
        i === selectedIdx ? applySerpentinePatch(it, patch) : it,
      );
      newItems = replaceChairsForParents(newItems, [selectedIdx], {
        chairSides: chairSides as ChairSides,
        defaultChairSizeFt: chairDepthFt,
      });
      const newIdx = findItemIndex(newItems, current);
      if (newIdx >= 0 && newIdx !== selectedIdx) setSelectedIdx(newIdx);
      setItems(newItems);
      recordChange(newItems);
    },
    [selectedIdx, recordChange, chairSides, chairDepthFt, findItemIndex],
  );

  const handleSharePlan = useCallback(() => {
    if (!selectedPlanId) return;
    shareFloorPlan.mutate(selectedPlanId, {
      onSuccess: (result) => {
        const fullUrl = `${window.location.origin}/api/v1/public/floor-plans/${result.shareToken}`;
        navigator.clipboard.writeText(fullUrl).catch(() => {});
        showToast('Share link copied to clipboard!');
      },
    });
  }, [selectedPlanId, shareFloorPlan, showToast]);

  const handleAssignSeat = useCallback(
    (guestId: string, seatNum: number) => {
      if (!selectedItem || !isTableItem(selectedItem)) return;
      const input = {
        guestId,
        tableNumber: selectedItem.tableNumber!,
        seatNumber: seatNum,
        ...(selectedPlanId ? { floorPlanId: selectedPlanId } : {}),
      };
      assignGuestToSeat.mutate(input, {
        onError: (err) =>
          handleSeatingRefusal(err, () =>
            assignGuestToSeat.mutate(
              { ...input, allowOverCapacity: true },
              {
                onError: (e) =>
                  showToast(extractApiErrorMessage(e, 'Could not seat the guest'), true),
              },
            ),
          ),
      });
    },
    [selectedItem, selectedPlanId, assignGuestToSeat, handleSeatingRefusal, showToast],
  );

  const handleZoomIn = useCallback(() => {
    setZoom((z) => Math.min(z + 0.2, 3));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom((z) => Math.max(z - 0.2, 0.2));
  }, []);

  // Exact zoom percentage — free typing, committed on blur/Enter and clamped
  // to the same 20–300% range as the +/- buttons.
  const commitZoomInput = useCallback(() => {
    const pct = parseInt(zoomInput, 10);
    const next = !isNaN(pct) ? Math.min(3, Math.max(0.2, pct / 100)) : zoom;
    setZoom(next);
    // Always normalize the text — setZoom alone won't re-sync when the
    // clamped value equals the current zoom.
    setZoomInput(String(Math.round(next * 100)));
  }, [zoomInput, zoom]);

  // Keep the zoom text field in sync when zoom changes via buttons/commit
  useEffect(() => {
    setZoomInput(String(Math.round(zoom * 100)));
  }, [zoom]);

  const handleBulkEditOpen = useCallback(() => {
    if (bulkEditTypesOf(items).length === 0) return;
    setShowBulkEditModal(true);
  }, [items]);

  const handleBulkEditApply = useCallback(
    (values: BulkEditValues) => {
      const {
        objectType: bulkEditObjectType,
        widthFt: bulkEditWidthFt,
        heightFt: bulkEditHeightFt,
        seats: bulkEditSeats,
        chairSizeFt: bulkEditChairSizeFt,
        label: bulkEditLabel,
        alwaysPlaceChairs: bulkEditAlwaysPlaceChairs,
      } = values;
      const currentItems = itemsRef.current;

      // Update all matching items; optionally update label
      const updatedItems = currentItems.map((it) => {
        if (it.objectType !== bulkEditObjectType) return it;
        const updated: CanvasItem = {
          ...it,
          widthFt: bulkEditWidthFt,
          heightFt: bulkEditHeightFt,
          seats: bulkEditSeats,
        };
        if (bulkEditLabel) updated.label = bulkEditLabel;
        return updated;
      });

      // For table-type items: remove old chairs and re-place them using the
      // NEW dimensions/seats (for tables that had chairs, or all if "always
      // place" is on).
      const parentIdxs = currentItems
        .map((it, i) => (it.objectType === bulkEditObjectType && isTableItem(it) ? i : -1))
        .filter((i) => i >= 0);

      if (parentIdxs.length > 0) {
        const finalItems = replaceChairsForParents(updatedItems, parentIdxs, {
          chairSides: chairSides as ChairSides,
          defaultChairSizeFt: bulkEditChairSizeFt,
          chairSizeFt: bulkEditChairSizeFt,
          placeIfMissing: bulkEditAlwaysPlaceChairs,
        });
        // Also sync the global chairDepthFt to the new chair size
        setChairDepthFt(bulkEditChairSizeFt);
        setItems(finalItems);
        recordChange(finalItems);
        saveItems(finalItems);
      } else {
        setItems(updatedItems);
        recordChange(updatedItems);
        saveItems(updatedItems);
      }

      const count = currentItems.filter((it) => it.objectType === bulkEditObjectType).length;
      setShowBulkEditModal(false);
      showToast(`Bulk updating ${count} object${count !== 1 ? 's' : ''}…`);
    },
    [chairSides, recordChange, showToast, saveItems],
  );

  // ─── Room Management ─────────────────────────────────────────

  /**
   * Rooms are set up on the venue. Leaving for it is a route change, which the
   * page's unload warning does not see, so unsaved layout is saved first — and
   * if it cannot be, the producer chooses whether to leave without it.
   */
  const goToVenueRooms = useCallback(async () => {
    if (!selectedVenueId) return;
    // Save until nothing is left unsaved: an edit made while a save is in
    // flight is not in that save, so one successful save is not enough.
    let saved = true;
    for (let attempt = 0; attempt < 3 && saved && floorPlanSave.hasUnsavedEdits(); attempt++) {
      saved = await floorPlanSave.save('QUIET');
    }
    if (
      floorPlanSave.hasUnsavedEdits() &&
      !(await confirmDialog({
        title: 'Leave without saving?',
        message: saved
          ? 'The plan changed while it was being saved. Your latest layout changes will be lost.'
          : 'The plan could not be saved. Your latest layout changes will be lost.',
        confirmLabel: 'Leave',
      }))
    ) {
      return;
    }
    navigate(`/venues/${selectedVenueId}`);
  }, [selectedVenueId, floorPlanSave, confirmDialog, navigate]);

  /** The room editor saved: the plan's canvas follows the room it is drawn in. */
  const handleRoomSaved = useCallback(
    (room: Room) => {
      if (selectedPlanId) {
        updateFloorPlanMutation.mutate({
          id: selectedPlanId,
          updates: {
            width: Math.round(room.widthFt * PIXELS_PER_FOOT),
            height: Math.round(room.heightFt * PIXELS_PER_FOOT),
          },
        });
      }
    },
    [selectedPlanId, updateFloorPlanMutation],
  );

  // ─── Duplicate ──────────────────────────────────────────────

  const handleOpenDuplicate = useCallback(() => {
    setShowDuplicateModal(true);
  }, []);

  const handleDuplicate = useCallback(
    (targetEventId: string, name: string | undefined) => {
      if (!selectedPlanId || !targetEventId) return;
      duplicateFloorPlan.mutate(
        {
          id: selectedPlanId,
          input: { targetEventId, name },
        },
        {
          onSuccess: () => {
            setShowDuplicateModal(false);
          },
        },
      );
    },
    [selectedPlanId, duplicateFloorPlan],
  );

  // ─── Event linking & PDF export ───────────────────────────────

  const handleLinkEvent = useCallback(
    (evId: string | null) => {
      if (!selectedPlanId) return;
      setLinkedEventId(evId);
      updateFloorPlanMutation.mutate({
        id: selectedPlanId,
        updates: { eventId: evId },
      });
    },
    [selectedPlanId, updateFloorPlanMutation],
  );

  const handlePrintPDF = useCallback(
    (options: FloorPlanPrintOptions) => {
      // Redraw the canvas light before it is copied, then put it back.
      flushSync(() => setPrintingCanvas(true));
      try {
        const svgEl = document.querySelector(
          '[data-testid="floor-plan-canvas"]',
        ) as SVGElement | null;

        const planEvent =
          currentEvent ??
          (loadedPlan?.event as
            | { name?: string; startAt?: string | Date | null }
            | null
            | undefined);
        const eventName = planEvent?.name ?? loadedPlan?.name ?? 'Floor Plan';
        const roomLabel = selectedRoom
          ? `${selectedRoom.name}${selectedRoom.roomType ? ` (${selectedRoom.roomType})` : ''} — ${formatLength(selectedRoom.widthFt, lengthUnits)} × ${formatLength(selectedRoom.heightFt, lengthUnits)}`
          : null;
        const venueName = selectedVenueId
          ? (venues.find((v) => v.id === selectedVenueId)?.name ??
            loadedPlan?.room?.venue?.name ??
            null)
          : (loadedPlan?.room?.venue?.name ?? null);

        printFloorPlan(
          svgEl,
          svgViewW,
          svgViewH,
          {
            eventName,
            eventDate: formatPrintDate(planEvent?.startAt),
            venueName,
            roomLabel,
            planName: loadedPlan?.name ?? null,
            appLogoUrl,
            tableCount: tableItems.length,
            totalSeats,
            personsAssigned: totalPersonsAssigned,
            familyGroupColors,
          },
          options,
        );
      } finally {
        setPrintingCanvas(false);
      }
    },
    [
      svgViewW,
      svgViewH,
      currentEvent,
      loadedPlan,
      selectedRoom,
      selectedVenueId,
      venues,
      tableItems,
      totalSeats,
      totalPersonsAssigned,
      familyGroupColors,
      appLogoUrl,
      lengthUnits,
    ],
  );

  // ─── B11 Handlers ─────────────────────────────────────────────

  const handleRotateItemBy = useCallback(
    (deg: number) => {
      if (selectedIdx === null) return;
      const newItems = itemsRef.current.map((it, i) =>
        i === selectedIdx ? { ...it, rotation: (it.rotation + deg + 360) % 360 } : it,
      );
      setItems(newItems);
      recordChange(newItems);
    },
    [selectedIdx, recordChange],
  );

  const handleNudgeItem = useCallback(
    (dx: number, dy: number) => {
      if (selectedIdx === null) return;
      // One grid step with snap on, a foot with it off.
      const step = snapEnabled ? gridSizeFt : 1;
      const pxDx = ftToPx(dx * step);
      const pxDy = ftToPx(dy * step);

      // Selection + groups + attached chairs all nudge together
      const moveSet = expandMoveSet(
        itemsRef.current,
        multiSelect.size > 0 ? new Set([selectedIdx, ...multiSelect]) : [selectedIdx],
      );
      const deltas = new Map<number, { dx: number; dy: number }>();
      moveSet.forEach((i) => deltas.set(i, { dx: pxDx, dy: pxDy }));
      const newItems = moveItemsWithChairs(itemsRef.current, deltas);
      if (refuseOverlap(itemsRef.current, newItems)) return;
      setItems(newItems);
      recordChange(newItems);
    },
    [selectedIdx, multiSelect, snapEnabled, gridSizeFt, recordChange, refuseOverlap],
  );

  const handleCloneItem = useCallback(() => {
    if (selectedIdx === null) return;
    const src = itemsRef.current[selectedIdx];
    const isTable = isTableItem(src);
    const newItem: CanvasItem = {
      ...src,
      id: undefined,
      uid: newUid(),
      // Clone starts independent — not in the original's group, and not
      // carrying the original's identity keys inside its config.
      groupId: undefined,
      config: copyItemConfig(src.config),
      x: src.x + ftToPx(src.widthFt) + 20,
      y: src.y,
      ...(isTable
        ? {
            tableNumber:
              Math.max(...itemsRef.current.filter(isTableItem).map((t) => t.tableNumber!)) + 1,
          }
        : {
            sortOrder: itemsRef.current.filter((it) => !isTableItem(it)).length,
          }),
    };
    const newItems = [...itemsRef.current, newItem];
    setItems(newItems);
    setSelectedIdx(newItems.length - 1);
    recordChange(newItems);
  }, [selectedIdx, recordChange]);

  const handleRemoveAllChairs = useCallback(() => {
    const chairCount = itemsRef.current.filter((it) => it.objectType === 'CHAIR').length;
    if (chairCount === 0) return;
    const newItems = itemsRef.current.filter((it) => it.objectType !== 'CHAIR');
    setItems(newItems);
    recordChange(newItems);
    showToast(`Removed ${chairCount} chairs`);
  }, [recordChange, showToast]);

  const handlePlaceAllChairs = useCallback(() => {
    const tables = itemsRef.current.filter(isTableItem);
    if (tables.length === 0) return;

    // Remove existing chairs first, then place fresh ones for all tables
    let newItems = itemsRef.current.filter((it) => it.objectType !== 'CHAIR');
    let totalChairs = 0;

    // Also clear old chair groupIds from tables
    newItems = newItems.map((it) =>
      isTableItem(it) && isChairGroupId(it.groupId) ? { ...it, groupId: undefined } : it,
    );

    for (let tIdx = 0; tIdx < newItems.length; tIdx++) {
      const table = newItems[tIdx];
      if (!isTableItem(table)) continue;
      const gid = table.groupId || newChairGroupId();
      const sides = tableChairSides(table, chairSides as ChairSides);
      const chairs = buildChairsForParent(table, {
        chairSizeFt: chairDepthFt,
        chairSides: sides,
        sortBase: newItems.length,
        groupId: gid,
      });
      // Assign groupId to the table too, and the sides its chairs are on
      newItems[tIdx] = withChairSides({ ...table, groupId: gid }, sides);
      newItems = [...newItems, ...chairs];
      totalChairs += chairs.length;
    }

    setItems(newItems);
    recordChange(newItems);
    showToast(`Placed ${totalChairs} chairs on ${tables.length} tables`);
  }, [chairDepthFt, chairSides, recordChange, showToast]);

  // ─── Keyboard Shortcuts ──────────────────────────────────────────
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return;
      // In Seating mode the plan does not change: its keys are the seating's.
      if (seatingModeOn && e.key !== 'f' && e.key !== 'F') return;

      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        if (selectedIdx !== null) void handleDeleteItem();
      } else if (e.key === 'Escape') {
        // Clear the selection first; a second Escape leaves focus mode.
        if (selectedIdx === null && multiSelect.size === 0) {
          setWorkspacePref({ focusMode: false });
        }
        setSelectedIdx(null);
        setMultiSelect(new Set());
      } else if ((e.key === 'f' || e.key === 'F') && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        setWorkspacePref({ focusMode: !focusMode });
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (selectedIdx !== null) handleNudgeItem(0, -1);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (selectedIdx !== null) handleNudgeItem(0, 1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (selectedIdx !== null) handleNudgeItem(-1, 0);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        if (selectedIdx !== null) handleNudgeItem(1, 0);
      } else if (e.key === 'z' && (e.ctrlKey || e.metaKey) && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      } else if (
        (e.key === 'y' && (e.ctrlKey || e.metaKey)) ||
        (e.key === 'z' && (e.ctrlKey || e.metaKey) && e.shiftKey)
      ) {
        e.preventDefault();
        handleRedo();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [
    seatingModeOn,
    selectedIdx,
    multiSelect,
    focusMode,
    setWorkspacePref,
    handleDeleteItem,
    handleNudgeItem,
    handleUndo,
    handleRedo,
  ]);

  const handleDirectionalClone = useCallback(() => {
    if (selectedIdx === null || cloneCount < 1) return;
    const src = itemsRef.current[selectedIdx];
    const srcIsTable = isTableItem(src);
    let maxNum = srcIsTable
      ? Math.max(...itemsRef.current.filter(isTableItem).map((t) => t.tableNumber!))
      : itemsRef.current.filter((it) => !isTableItem(it)).length;
    const clones: CanvasItem[] = [];
    for (let i = 1; i <= cloneCount; i++) {
      maxNum++;
      clones.push({
        ...src,
        id: undefined,
        uid: newUid(),
        groupId: undefined,
        config: copyItemConfig(src.config),
        ...(srcIsTable ? { tableNumber: maxNum } : { sortOrder: maxNum }),
        x:
          cloneDirection === 'right'
            ? src.x + i * (ftToPx(src.widthFt) + ftToPx(cloneSpacingFt))
            : src.x,
        y:
          cloneDirection === 'below'
            ? src.y + i * (ftToPx(src.heightFt) + ftToPx(cloneSpacingFt))
            : src.y,
      });
    }
    const newItems = [...itemsRef.current, ...clones];
    setItems(newItems);
    recordChange(newItems);
    showToast(`Cloned ${cloneCount} ${srcIsTable ? 'tables' : 'objects'}`);
  }, [selectedIdx, cloneCount, cloneDirection, cloneSpacingFt, recordChange, showToast]);

  const handlePlaceCeremonyChairs = useCallback(
    (opts: CeremonyChairsOptions) => {
      const {
        preset: ceremonyPreset,
        rows: ceremonyRows,
        chairsPerRow: ceremonyChairsPerRow,
      } = opts;
      const { positions, widthFt, heightFt } = calcCeremonyPositions(
        opts.preset,
        opts.rows,
        opts.chairsPerRow,
        opts.chairSpacingFt,
        opts.rowSpacingFt,
        opts.withAisle,
        opts.aisleWidthFt,
      );
      const nonTableCount = itemsRef.current.filter((it) => !isTableItem(it)).length;
      const newItem: CanvasItem = {
        objectType: 'CEREMONY_CHAIRS',
        label: `Ceremony Chairs (${ceremonyRows * ceremonyChairsPerRow})`,
        x: ftToPx(2.5 + nonTableCount * 0.5),
        y: ftToPx(2.5 + nonTableCount * 0.5),
        widthFt,
        heightFt,
        rotation: 0,
        color: '#d1d5db',
        opacity: 1,
        seats: positions.length,
        config: {
          preset: ceremonyPreset,
          positions,
          rows: ceremonyRows,
          chairsPerRow: ceremonyChairsPerRow,
        },
        sortOrder: nonTableCount,
        uid: newUid(),
      };
      const newItems = [...itemsRef.current, newItem];
      setItems(newItems);
      setSelectedIdx(newItems.length - 1);
      setActivePanel('properties');
      setShowCeremonyDialog(false);
      recordChange(newItems);
    },
    [recordChange],
  );

  // ─── Loading ──────────────────────────────────────────────────

  if (loadingPlans) {
    return (
      <div className="p-6">
        <p className="text-gray-500">Loading floor plans...</p>
      </div>
    );
  }

  // ─── Render ───────────────────────────────────────────────────

  return (
    <div
      data-testid="floor-plan-page"
      className={
        focusMode
          ? 'fixed inset-0 z-40 flex flex-col gap-2 overflow-hidden bg-gray-50 p-2'
          : undefined
      }
    >
      {/* Back button — shown when navigated from an event or venue detail page */}
      {!focusMode && !inWorkspace && (eventId || fromVenueId) && (
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-3"
          data-testid="back-btn"
        >
          <ArrowLeft size={16} />
          {eventId
            ? currentEvent
              ? `Back to ${currentEvent.name}`
              : 'Back to Event'
            : fromVenueId
              ? `Back to ${venues.find((v) => v.id === fromVenueId)?.name ?? 'Venue'}`
              : 'Back'}
        </button>
      )}

      {/* Header */}
      <div className={`flex items-center justify-between mb-4 ${focusMode ? 'hidden' : ''}`}>
        <h1 className="text-2xl font-bold text-gray-900">
          <Grid3X3 className="inline mr-2" size={24} />
          Floor Plan & Seating
        </h1>
        <div className="flex gap-2">
          <select
            value={selectedVenueId || ''}
            onChange={(e) => {
              setSelectedVenueId(e.target.value || null);
              setSelectedRoomId(null);
            }}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm"
            data-testid="venue-select"
          >
            <option value="">All venues</option>
            {venues.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </select>
          <select
            value={selectedRoomId || ''}
            onChange={(e) => {
              const rid = e.target.value || null;
              setSelectedRoomId(rid);
              if (rid) {
                const room = rooms.find((r: Room) => r.id === rid);
                if (room?.venueId) setSelectedVenueId(room.venueId);
              }
              if (selectedPlanId) {
                const planId = selectedPlanId;
                const save = { planId, roomId: rid };
                roomSaveRef.current = save;
                updateFloorPlanMutation.mutate(
                  { id: planId, updates: { roomId: rid } },
                  {
                    onSettled: () => {
                      // Only the latest pick's save lets the plan's room through again.
                      if (roomSaveRef.current === save) roomSaveRef.current = null;
                    },
                    onError: () => {
                      showToast('The room could not be saved on the plan', true);
                      // Back to what the plan stores, if it is still on screen.
                      const stored = loadedPlanRoomRef.current;
                      if (stored.planId === planId && selectedPlanIdRef.current === planId) {
                        setSelectedRoomId(stored.roomId);
                      }
                    },
                  },
                );
              }
            }}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm"
            data-testid="room-select"
          >
            <option value="">No room selected</option>
            {rooms.map((r: Room) => (
              <option key={r.id} value={r.id}>
                {r.name}
                {r.roomType ? ` [${r.roomType}]` : ''} ({formatLength(r.widthFt, lengthUnits)} x{' '}
                {formatLength(r.heightFt, lengthUnits)})
              </option>
            ))}
          </select>
          {selectedRoomId ? (
            <button
              onClick={() => setShowShapeEditor(true)}
              className="flex items-center gap-1 px-2 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
              data-testid="edit-room-shape-btn"
              aria-label="Edit room shape and fixtures"
              title="Edit the room's shape and fixtures — the same room editor as on the venue"
            >
              <DraftingCompass size={14} />
            </button>
          ) : null}
          {selectedVenueId && (
            <button
              onClick={() => void goToVenueRooms()}
              className="flex items-center gap-1 px-2 py-2 text-sm text-primary-600 hover:underline"
              data-testid="venue-rooms-link"
              title="Rooms are set up on the venue: their size, shape and fixtures"
            >
              {rooms.length === 0 ? 'Add rooms on the venue' : 'Venue rooms'}
            </button>
          )}
          <button
            onClick={handleCreatePlan}
            className="flex items-center gap-1 px-3 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700"
            data-testid="create-plan-btn"
          >
            <Plus size={16} />
            New Plan
          </button>
        </div>
      </div>

      {/* The event's plans as the parts of its evening, once any is set to one */}
      {eventId && hasPlanParts(orderedPlans) && (
        <PlanPartTabs
          plans={orderedPlans}
          parts={eveningParts}
          activeId={selectedPlanId}
          onSelect={setSelectedPlanId}
          onAddPart={() => setPartModal('new')}
        />
      )}

      {/* Plan selector */}
      {floorPlans.length > 0 && (
        <div
          className={`flex items-center gap-2 overflow-x-auto flex-wrap ${focusMode ? 'mb-0' : 'mb-4'}`}
          data-testid="plan-selector"
        >
          <select
            value={selectedPlanId || ''}
            onChange={(e) => setSelectedPlanId(e.target.value || null)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm"
            data-testid="plan-select"
          >
            {orderedPlans.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
                {describePlanPart(p, eveningParts) ? ` · ${describePlanPart(p, eveningParts)}` : ''}
                {p.room?.venue?.name ? ` · ${p.room.venue.name}` : ''}
                {p.room?.name ? ` / ${p.room.name}` : ''}
                {p.event?.name ? ` [${p.event.name}]` : ''}
              </option>
            ))}
          </select>
          {selectedPlanId && (
            <>
              <PlanPartButton
                description={loadedPlan ? describePlanPart(loadedPlan, eveningParts) : null}
                compact={focusMode}
                onClick={() => setPartModal('edit')}
              />
              <button
                onClick={handleSave}
                disabled={
                  !floorPlanSave.isDirty || floorPlanSave.isSaving || !!floorPlanSave.conflict
                }
                className="flex items-center gap-1 px-3 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 disabled:opacity-50"
                data-testid="save-btn"
              >
                <Save size={14} />
                Save
              </button>
              <span
                className={`text-xs ${floorPlanSave.conflict ? 'text-amber-700' : floorPlanSave.isDirty ? 'text-gray-600' : 'text-gray-400'}`}
                aria-live="polite"
                data-testid="save-status"
              >
                {saveStatusLabel({
                  isSaving: floorPlanSave.isSaving,
                  isDirty: floorPlanSave.isDirty,
                  hasConflict: !!floorPlanSave.conflict,
                  lastSavedAt: floorPlanSave.lastSavedAt,
                })}
              </span>
              <button
                onClick={() => setShowHistoryModal(true)}
                className="flex items-center gap-1 px-3 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
                data-testid="history-btn"
                title="Version history"
              >
                <History size={14} />
                {!focusMode && 'History'}
              </button>
              <button
                onClick={handleOpenSaveAs}
                className="flex items-center gap-1 px-3 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
                data-testid="save-as-btn"
              >
                <Copy size={14} />
                {!focusMode && 'Save As…'}
              </button>
              <button
                onClick={handleSaveAsTemplate}
                className="flex items-center gap-1 px-3 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
                data-testid="save-template-btn"
              >
                <Copy size={14} />
                {!focusMode && 'Save as Template'}
              </button>
              <button
                onClick={() => setShowAutoAssignModal(true)}
                disabled={!selectedPlanId || !eventId}
                title={
                  selectedPlanId && eventId
                    ? 'Auto-assign confirmed guests to tables'
                    : 'Open a saved event-scoped floor plan first'
                }
                className="flex items-center gap-1 px-3 py-2 border border-purple-300 text-purple-700 text-sm rounded-md hover:bg-purple-50 disabled:opacity-50"
                data-testid="auto-assign-btn"
              >
                <Sparkles size={14} />
                {!focusMode && 'Auto-Assign Seats'}
              </button>
              {selectedPlanId && eventId && (
                <SeatingPlanControls
                  state={seatingState?.plan.id === selectedPlanId ? seatingState : undefined}
                  onMakePrimary={handleMakePrimarySeating}
                  onToggleLock={handleToggleSeatingLock}
                  busy={lockSeating.isPending || setPrimarySeating.isPending}
                  showLabels={!focusMode}
                />
              )}
              {selectedPlanId && eventId && (
                <button
                  type="button"
                  onClick={toggleSeatingMode}
                  disabled={!planSeating}
                  aria-pressed={seatingModeOn}
                  title={
                    seatingModeOn
                      ? 'Back to editing the plan'
                      : 'Seat guests on the plan — nothing on it moves'
                  }
                  className={`flex items-center gap-1 px-3 py-2 border text-sm rounded-md disabled:opacity-50 ${
                    seatingModeOn
                      ? 'border-primary-600 bg-primary-600 text-white hover:bg-primary-700'
                      : 'border-primary-300 text-primary-700 hover:bg-primary-50'
                  }`}
                  data-testid="seating-mode-toggle"
                >
                  <UserCheck size={14} />
                  {!focusMode && (seatingModeOn ? 'Seating mode' : 'Seat guests')}
                </button>
              )}
              {aiSeatingEnabled && selectedPlanId && eventId && (
                <AiSeatingButton onClick={() => setShowAiSeatingModal(true)} />
              )}
              {aiRenderEnabled && selectedPlanId && (
                <RenderStudioButton onClick={() => setShowRenderStudio(true)} />
              )}
              <button
                onClick={handleOpenDuplicate}
                className="flex items-center gap-1 px-3 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
                data-testid="duplicate-btn"
              >
                <Copy size={14} />
                {!focusMode && 'Duplicate'}
              </button>
              <button
                onClick={handleSharePlan}
                className="flex items-center gap-1 px-3 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
                data-testid="share-btn"
              >
                <Share2 size={14} />
                {!focusMode && 'Share'}
              </button>
              {/* Link to Event (only when not navigated from event page) */}
              {!eventId && (
                <div className="flex items-center gap-1" data-testid="link-event-container">
                  <Link2 size={14} className="text-gray-500 flex-shrink-0" />
                  <select
                    value={linkedEventId || ''}
                    onChange={(e) => handleLinkEvent(e.target.value || null)}
                    className="px-2 py-2 border border-gray-300 rounded-md text-sm max-w-[160px]"
                    data-testid="link-event-select"
                  >
                    <option value="">Link to event…</option>
                    {allEvents.map((ev) => (
                      <option key={ev.id} value={ev.id}>
                        {ev.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <button
                onClick={() => setShow3DModal(true)}
                className="flex items-center gap-1 px-3 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
                data-testid="view-3d-btn"
                title="3D view"
              >
                <Box size={14} />
                {!focusMode && '3D'}
              </button>
              {lookEventId && (
                <button
                  onClick={() => setShowLook(true)}
                  className="flex items-center gap-1 px-3 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
                  data-testid="decor-look-btn"
                  title="The look: linen, chairs, flowers, tableware, walls and floor"
                >
                  <Palette size={14} />
                  {!focusMode && 'Look'}
                </button>
              )}
              <button
                onClick={() => setShowPrintModal(true)}
                className="flex items-center gap-1 px-3 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
                data-testid="print-pdf-btn"
                title="Export PDF"
              >
                <Printer size={14} />
                {!focusMode && 'PDF'}
              </button>
              <button
                onClick={() => setWorkspacePref({ focusMode: !focusMode })}
                className={`flex items-center gap-1 px-3 py-2 border text-sm rounded-md ${
                  focusMode
                    ? 'bg-primary-100 border-primary-300 text-primary-700'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
                data-testid="focus-mode-btn"
                title={
                  focusMode
                    ? 'Exit full-screen editing (F or Esc)'
                    : 'Full-screen editing — hide the page chrome (F)'
                }
              >
                {focusMode ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
                {focusMode ? 'Exit' : 'Focus'}
              </button>
              <button
                onClick={() => setShowObjectList((v) => !v)}
                className={`flex items-center gap-1 px-3 py-2 border text-sm rounded-md ${
                  showObjectList
                    ? 'bg-primary-100 border-primary-300 text-primary-700'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
                data-testid="object-list-btn"
                title="Object list"
              >
                <List size={14} />
                {!focusMode && 'Objects'}
              </button>
              {eventId && (
                <a
                  href={`/api/v1/export/events/${eventId}/floor-plan-setup`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 px-3 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
                  data-testid="export-setup-btn"
                >
                  <Download size={14} />
                  {!focusMode && 'Setup Report'}
                </a>
              )}
              <button
                onClick={handleDeletePlan}
                className="flex items-center gap-1 px-3 py-2 text-red-600 text-sm rounded-md hover:bg-red-50"
                data-testid="delete-plan-btn"
              >
                <Trash2 size={14} />
              </button>
            </>
          )}
        </div>
      )}

      {floorPlanSave.conflict && (
        <SaveConflictBanner
          conflict={floorPlanSave.conflict}
          busy={floorPlanSave.isSaving}
          onLoadTheirs={handleLoadTheirs}
          onKeepMine={floorPlanSave.overwrite}
        />
      )}

      {/* Unsaved work banner — designing without a floor plan record */}
      {!selectedPlanId && items.length > 0 && (
        <div
          className="flex items-center justify-between gap-3 mb-4 px-3 py-2 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-800"
          data-testid="unsaved-plan-banner"
        >
          <span>
            This design is not attached to a floor plan yet — save it before leaving the page.
          </span>
          <button
            onClick={handleOpenSaveAs}
            className="flex items-center gap-1 px-3 py-1.5 bg-amber-600 text-white text-sm rounded-md hover:bg-amber-700 flex-shrink-0"
            data-testid="save-as-unsaved-btn"
          >
            <Save size={14} />
            Save As…
          </button>
        </div>
      )}

      {/* Templates bar */}
      {!focusMode && templates.length > 0 && (
        <div className="mb-4 text-sm text-gray-600" data-testid="templates-bar">
          <span className="font-medium">Templates: </span>
          {templates.map((t) => (
            <span key={t.id} className="mr-2 text-primary-600">
              {t.templateName || t.name}
            </span>
          ))}
        </div>
      )}

      {/* Main layout */}
      <SeatingDnd parties={seatingMode.actions.parties} onDragEnd={seatingMode.onDragEnd}>
        <div
          className={`flex gap-2 ${focusMode ? 'flex-1 min-h-0' : ''}`}
          style={focusMode ? undefined : { height: 'calc(100vh - 220px)' }}
          data-testid="floor-plan-layout"
        >
          {/* Left panel — Object Library */}
          <CollapsibleSidePanel
            side="left"
            collapsed={libraryCollapsed}
            onToggle={() => setWorkspacePref({ libraryCollapsed: !libraryCollapsed })}
            label={seatingMode.active ? 'Seat guests' : 'Object Library'}
            expandedClassName={seatingMode.active ? 'w-72 p-2' : 'w-48 p-2'}
            header={
              <h3 className="text-sm font-semibold text-gray-700">
                {seatingMode.active ? 'Seat guests' : 'Object Library'}
              </h3>
            }
            testId="object-library"
            toggleTestId="toggle-library-btn"
          >
            {seatingMode.active ? (
              <SeatingModePanel mode={seatingMode} items={items} />
            ) : (
              <>
                {/* Search */}
                <div className="relative mb-2">
                  <Search
                    size={12}
                    className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400"
                  />
                  <input
                    type="text"
                    value={libSearch}
                    onChange={(e) => setLibSearch(e.target.value)}
                    placeholder="Search..."
                    className="w-full pl-6 pr-2 py-1 border border-gray-300 rounded text-xs"
                    data-testid="lib-search"
                  />
                </div>

                {/* Ceremony Chairs button */}
                <button
                  onClick={() => setShowCeremonyDialog(true)}
                  className="w-full mb-3 px-2 py-1.5 border border-purple-200 bg-purple-50 text-purple-700 rounded text-xs hover:bg-purple-100 text-left flex items-center gap-1"
                  data-testid="ceremony-chairs-btn"
                >
                  <Armchair size={12} />
                  Ceremony Chairs...
                </button>

                <LibraryCategoryTabs
                  categories={libraryCategories}
                  selected={libraryCategory}
                  onSelect={setLibraryCategory}
                />

                {/* Library items */}
                <LibraryItemList
                  key={`${libraryCategory}|${libSearch}`}
                  lengthUnits={lengthUnits}
                  items={libraryItems}
                  prefs={libraryPrefs.prefs}
                  showCategory={virtualCategory}
                  onAdd={(item) => {
                    handleAddObject(item);
                    libraryPrefs.recordUse(item);
                  }}
                  onToggleFavourite={libraryPrefs.toggleFavourite}
                  emptyMessage={
                    virtualCategory && libSearch ? 'Nothing here matches the search.' : undefined
                  }
                />
              </>
            )}
          </CollapsibleSidePanel>

          {/* Canvas */}
          <div className="flex-1 bg-white rounded-lg shadow overflow-hidden flex flex-col relative">
            {/* Floating Object List Panel */}
            {showObjectList && (
              <div
                className="absolute z-20 w-72 bg-white border border-gray-200 rounded-lg shadow-lg flex flex-col"
                style={{ maxHeight: 'calc(100% - 16px)', left: objListPos.x, top: objListPos.y }}
              >
                <div
                  className="flex items-center justify-between px-3 py-2 border-b border-gray-100 flex-shrink-0 select-none"
                  style={{ cursor: 'grab' }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    objListDragRef.current = {
                      startMouseX: e.clientX,
                      startMouseY: e.clientY,
                      startPosX: objListPos.x,
                      startPosY: objListPos.y,
                    };
                    const onMove = (me: MouseEvent) => {
                      if (!objListDragRef.current) return;
                      const dx = me.clientX - objListDragRef.current.startMouseX;
                      const dy = me.clientY - objListDragRef.current.startMouseY;
                      setObjListPos({
                        x: Math.max(0, objListDragRef.current.startPosX + dx),
                        y: Math.max(0, objListDragRef.current.startPosY + dy),
                      });
                    };
                    const onUp = () => {
                      objListDragRef.current = null;
                      window.removeEventListener('mousemove', onMove);
                      window.removeEventListener('mouseup', onUp);
                    };
                    window.addEventListener('mousemove', onMove);
                    window.addEventListener('mouseup', onUp);
                  }}
                >
                  <span className="text-sm font-semibold text-gray-700">
                    Objects ({filteredObjectList.length}/{items.length})
                  </span>
                  <button
                    onClick={() => setShowObjectList(false)}
                    className="text-gray-400 hover:text-gray-600"
                    data-testid="object-list-close-btn"
                    aria-label="Close object list"
                  >
                    <X size={14} />
                  </button>
                </div>

                {/* Search */}
                <div className="p-2 border-b border-gray-100 flex-shrink-0 space-y-1">
                  <div className="relative">
                    <Search
                      size={12}
                      className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400"
                    />
                    <input
                      type="text"
                      value={objectListSearch}
                      onChange={(e) => setObjectListSearch(e.target.value)}
                      placeholder="Search by name or type..."
                      className="w-full pl-6 pr-2 py-1 border border-gray-300 rounded text-xs"
                    />
                  </div>

                  {/* Type filter */}
                  <div className="relative">
                    <button
                      onClick={() => setShowObjectListTypeMenu((v) => !v)}
                      className="w-full flex items-center justify-between px-2 py-1 border border-gray-300 rounded text-xs text-gray-600 hover:bg-gray-50"
                    >
                      <span className="flex items-center gap-1">
                        <Filter size={10} />
                        {objectListTypeFilter.length === 0
                          ? 'All types'
                          : `${objectListTypeFilter.length} type${objectListTypeFilter.length > 1 ? 's' : ''} selected`}
                      </span>
                      <ChevronDown size={10} />
                    </button>
                    {showObjectListTypeMenu && (
                      <div className="absolute z-30 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded shadow-lg max-h-40 overflow-auto">
                        {objectTypeOptions.map((type) => (
                          <label
                            key={type}
                            className="flex items-center gap-2 px-2 py-1 text-xs hover:bg-gray-50 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={objectListTypeFilter.includes(type)}
                              onChange={(e) => {
                                setObjectListTypeFilter((prev) =>
                                  e.target.checked
                                    ? [...prev, type]
                                    : prev.filter((t) => t !== type),
                                );
                              }}
                              className="h-3 w-3 rounded"
                            />
                            <span className="truncate">{type}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>

                  {objectListTypeFilter.length > 0 && (
                    <button
                      onClick={() => setObjectListTypeFilter([])}
                      className="text-xs text-primary-600 hover:underline"
                    >
                      Clear filter
                    </button>
                  )}
                </div>

                {/* Object rows */}
                <div className="overflow-auto flex-1">
                  {filteredObjectList.length === 0 ? (
                    <div className="text-xs text-gray-400 text-center py-4">No objects found</div>
                  ) : (
                    filteredObjectList.map(({ item, idx }) => {
                      const displayName =
                        item.label ||
                        (isTableItem(item) ? `Table ${item.tableNumber}` : item.objectType);
                      return (
                        <button
                          key={idx}
                          onClick={() => {
                            setSelectedIdx(idx);
                            setActivePanel('properties');
                            setShowObjectListTypeMenu(false);
                          }}
                          className={`w-full text-left px-3 py-1.5 text-xs border-b border-gray-50 hover:bg-blue-50 transition-colors ${
                            selectedIdx === idx ? 'bg-blue-100 text-blue-700' : 'text-gray-700'
                          }`}
                        >
                          <div className="font-medium truncate">{displayName}</div>
                          <div className="text-gray-400 text-[10px]">
                            {item.objectType} · {Math.round(pxToFt(item.x) * 10) / 10}ft,{' '}
                            {Math.round(pxToFt(item.y) * 10) / 10}ft
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            )}

            {/* Horizontal ruler */}
            <Ruler direction="horizontal" lengthPx={canvasWidthPx} zoom={zoom} />

            <div className="flex flex-1 min-h-0 overflow-hidden">
              {/* Vertical ruler */}
              <Ruler direction="vertical" lengthPx={canvasHeightPx} zoom={zoom} />

              {/* SVG canvas */}
              <div className="overflow-auto flex-1">
                <CanvasPaletteContext.Provider value={canvasPalette}>
                  <svg
                    width={svgDisplayW}
                    height={svgDisplayH}
                    viewBox={`0 0 ${svgViewW} ${svgViewH}`}
                    className="border border-gray-200"
                    style={{ background: canvasPalette.background }}
                    data-testid="floor-plan-canvas"
                    onClick={handleCanvasClick}
                    onMouseDown={handleCanvasMouseDown}
                    onMouseMove={handleCanvasMouseMove}
                    onMouseUp={handleCanvasMouseUp}
                  >
                    <g transform={rotGroupTransform}>
                      {/* Room outline */}
                      {roomShape ? (
                        <RoomShapeLayer shape={roomShape} scale={PIXELS_PER_FOOT} />
                      ) : selectedRoom?.perimeterPoints &&
                        selectedRoom.perimeterPoints.length >= 3 ? (
                        <polygon
                          points={selectedRoom.perimeterPoints
                            .map((p) => `${ftToPx(p.x)},${ftToPx(p.y)}`)
                            .join(' ')}
                          fill="none"
                          stroke={canvasPalette.wall}
                          strokeWidth={3}
                        />
                      ) : (
                        <rect
                          x={0}
                          y={0}
                          width={canvasWidthPx}
                          height={canvasHeightPx}
                          fill="none"
                          stroke={canvasPalette.wall}
                          strokeWidth={3}
                        />
                      )}

                      {/* Room dimensions */}
                      {selectedRoom && (
                        <>
                          <text
                            x={canvasWidthPx / 2}
                            y={-5}
                            textAnchor="middle"
                            fontSize="11"
                            fill={canvasPalette.labelMuted}
                          >
                            {formatLength(selectedRoom.widthFt, lengthUnits)}
                          </text>
                          <text
                            x={-5}
                            y={canvasHeightPx / 2}
                            textAnchor="middle"
                            fontSize="11"
                            fill={canvasPalette.labelMuted}
                            transform={`rotate(-90, -5, ${canvasHeightPx / 2})`}
                          >
                            {formatLength(selectedRoom.heightFt, lengthUnits)}
                          </text>
                        </>
                      )}

                      {/* Room entries (doors) */}
                      {selectedRoom?.entries?.map((entry) => {
                        let ex = 0,
                          ey = 0,
                          ew = 0,
                          eh = 0;
                        const gapPx = ftToPx(entry.widthFt);
                        if (entry.wall === 'north') {
                          ex = entry.position * canvasWidthPx - gapPx / 2;
                          ey = -2;
                          ew = gapPx;
                          eh = 6;
                        } else if (entry.wall === 'south') {
                          ex = entry.position * canvasWidthPx - gapPx / 2;
                          ey = canvasHeightPx - 4;
                          ew = gapPx;
                          eh = 6;
                        } else if (entry.wall === 'west') {
                          ex = -2;
                          ey = entry.position * canvasHeightPx - gapPx / 2;
                          ew = 6;
                          eh = gapPx;
                        } else {
                          ex = canvasWidthPx - 4;
                          ey = entry.position * canvasHeightPx - gapPx / 2;
                          ew = 6;
                          eh = gapPx;
                        }
                        return (
                          <g key={entry.id}>
                            <rect
                              x={ex}
                              y={ey}
                              width={ew}
                              height={eh}
                              fill={canvasPalette.background}
                              stroke="#16a34a"
                              strokeWidth={2}
                            />
                            <text
                              x={ex + ew / 2}
                              y={ey + eh + 10}
                              textAnchor="middle"
                              fontSize="8"
                              fill="#16a34a"
                            >
                              {entry.name}
                            </text>
                          </g>
                        );
                      })}

                      {showGrid && (
                        <GridLines
                          widthPx={canvasWidthPx}
                          heightPx={canvasHeightPx}
                          gridSizeFt={gridSizeFt}
                        />
                      )}

                      {/* Canvas items (objects first, then tables for z-order) */}
                      {items.map((item, idx) => {
                        // When family colors are on, apply the guest's family color to placed
                        // CHAIR items so they match the renderChairPreview colors instead of
                        // rendering gray on top and washing out the preview colors.
                        let displayItem = item;
                        if (showFamilyColors && item.objectType === 'CHAIR') {
                          const parentUid = item.config?.[CHAIR_PARENT_FIELD];
                          const tableNum =
                            typeof parentUid === 'string'
                              ? itemsByUid.get(parentUid)?.tableNumber
                              : undefined;
                          if (tableNum != null) {
                            const seatNum = parseInt(String(item.label ?? '').replace('S', ''));
                            const seatColor = !isNaN(seatNum)
                              ? tableSeatColors[tableNum]?.[seatNum]
                              : undefined;
                            if (seatColor) displayItem = { ...item, color: seatColor };
                          }
                        }
                        // Live drag preview: co-moving items follow the cursor.
                        // Floor on the visible box (rotation-aware) so the preview
                        // matches where the item settles on drop.
                        if (dragPreview && dragPreview.set.has(idx)) {
                          const floor = visualAnchorFloor(
                            ftToPx(item.widthFt),
                            ftToPx(item.heightFt),
                            item.rotation,
                          );
                          displayItem = {
                            ...displayItem,
                            x: Math.max(floor.x, item.x + dragPreview.dx),
                            y: Math.max(floor.y, item.y + dragPreview.dy),
                          };
                        }
                        return (
                          <CanvasItemShape
                            lengthUnits={lengthUnits}
                            key={`item-${item.id || item.objectType}-${idx}`}
                            item={displayItem}
                            index={idx}
                            seated={
                              isTableItem(item) ? seatedPerTable[item.tableNumber!] || 0 : undefined
                            }
                            isSelected={selectedIdx === idx || multiSelect.has(idx)}
                            onClick={handleShapeClick}
                            onDragEnd={handleShapeDragEnd}
                            onDragMove={handleShapeDragMove}
                            scale={zoom}
                            showChairs={isTableItem(item) ? showChairs : undefined}
                            showLabel={showLabels}
                            showTableNumbers={isTableItem(item) ? showTableNumbers : undefined}
                            showIcons={showIcons}
                            chairSizeFt={isTableItem(item) ? chairDepthFt : undefined}
                            chairSides={
                              isTableItem(item)
                                ? (chairSides as import('@peles/shared').ChairSides)
                                : undefined
                            }
                            groupColor={
                              isTableItem(item) && tableGroups[item.tableNumber!]
                                ? TABLE_GROUP_COLORS[tableGroups[item.tableNumber!]]
                                : undefined
                            }
                            seatColors={
                              isTableItem(item) && showFamilyColors
                                ? tableSeatColors[item.tableNumber!]
                                : undefined
                            }
                            canvasRotation={canvasRotation}
                            interactive={!seatingMode.active}
                          />
                        );
                      })}

                      {seatingMode.active && (
                        <SeatingCanvasLayer
                          mode={seatingMode}
                          items={items}
                          chairSizeFt={chairDepthFt}
                          chairSides={chairSides as ChairSides}
                        />
                      )}

                      {/* Measure line */}
                      {measureMode && measurePoints.length === 2 && (
                        <g data-testid="measure-line">
                          <line
                            x1={measurePoints[0].x}
                            y1={measurePoints[0].y}
                            x2={measurePoints[1].x}
                            y2={measurePoints[1].y}
                            stroke="#ef4444"
                            strokeWidth={2}
                            strokeDasharray="6 3"
                          />
                          <circle
                            cx={measurePoints[0].x}
                            cy={measurePoints[0].y}
                            r={4}
                            fill="#ef4444"
                          />
                          <circle
                            cx={measurePoints[1].x}
                            cy={measurePoints[1].y}
                            r={4}
                            fill="#ef4444"
                          />
                          <text
                            x={(measurePoints[0].x + measurePoints[1].x) / 2}
                            y={(measurePoints[0].y + measurePoints[1].y) / 2 - 8}
                            textAnchor="middle"
                            fontSize="12"
                            fontWeight="bold"
                            fill="#ef4444"
                          >
                            {formatLength(
                              calculateDistanceFt(
                                pxToFt(measurePoints[0].x),
                                pxToFt(measurePoints[0].y),
                                pxToFt(measurePoints[1].x),
                                pxToFt(measurePoints[1].y),
                              ),
                              lengthUnits,
                            )}
                          </text>
                        </g>
                      )}

                      {/* Rubber-band selection rect */}
                      {rubberBand && (Math.abs(rubberBand.w) > 3 || Math.abs(rubberBand.h) > 3) && (
                        <rect
                          x={rubberBand.w >= 0 ? rubberBand.x : rubberBand.x + rubberBand.w}
                          y={rubberBand.h >= 0 ? rubberBand.y : rubberBand.y + rubberBand.h}
                          width={Math.abs(rubberBand.w)}
                          height={Math.abs(rubberBand.h)}
                          fill="rgba(37, 99, 235, 0.1)"
                          stroke={canvasPalette.selection}
                          strokeWidth={1}
                          strokeDasharray="4 2"
                          pointerEvents="none"
                        />
                      )}

                      {showSpacing && (
                        <SpacingOverlay issues={spacingIssues} footprints={spacingFootprints} />
                      )}

                      {/* Multi-select highlight */}
                      {multiSelect.size > 0 &&
                        [...multiSelect].map((idx) => {
                          let it = items[idx];
                          if (!it) return null;
                          if (dragPreview && dragPreview.set.has(idx)) {
                            const floor = visualAnchorFloor(
                              ftToPx(it.widthFt),
                              ftToPx(it.heightFt),
                              it.rotation,
                            );
                            it = {
                              ...it,
                              x: Math.max(floor.x, it.x + dragPreview.dx),
                              y: Math.max(floor.y, it.y + dragPreview.dy),
                            };
                          }
                          return (
                            <rect
                              key={`ms-${idx}`}
                              x={it.x - 3}
                              y={it.y - 3}
                              width={ftToPx(it.widthFt) + 6}
                              height={ftToPx(it.heightFt) + 6}
                              fill="none"
                              stroke={canvasPalette.selection}
                              strokeWidth={1.5}
                              strokeDasharray="4 2"
                              rx={4}
                              pointerEvents="none"
                              transform={`rotate(${it.rotation}, ${it.x + ftToPx(it.widthFt) / 2}, ${it.y + ftToPx(it.heightFt) / 2})`}
                            />
                          );
                        })}

                      {/* Group legend */}
                      {Object.keys(tableGroups).length > 0 && (
                        <g transform={`translate(${canvasWidthPx - 130}, 10)`}>
                          <rect
                            width={120}
                            height={Object.keys(TABLE_GROUP_COLORS).length * 16 + 20}
                            rx={4}
                            fill={canvasPalette.panel}
                            stroke={canvasPalette.panelBorder}
                          />
                          <text
                            x={10}
                            y={14}
                            fontSize="10"
                            fontWeight="bold"
                            fill={canvasPalette.label}
                          >
                            Groups
                          </text>
                          {(Object.entries(TABLE_GROUP_COLORS) as [TableGroup, string][]).map(
                            ([group, color], i) => (
                              <g key={group} transform={`translate(10, ${22 + i * 16})`}>
                                <rect width={10} height={10} rx={2} fill={color} />
                                <text x={16} y={9} fontSize="9" fill={canvasPalette.labelMuted}>
                                  {group}
                                </text>
                              </g>
                            ),
                          )}
                        </g>
                      )}

                      {/* Family group legend */}
                      {showFamilyColors && Object.keys(familyGroupColors).length > 0 && (
                        <g
                          transform={`translate(${canvasWidthPx - 150}, ${Object.keys(tableGroups).length > 0 ? Object.keys(TABLE_GROUP_COLORS).length * 16 + 40 : 10})`}
                          data-testid="family-legend"
                        >
                          <rect
                            width={140}
                            height={Object.keys(familyGroupColors).length * 16 + 20}
                            rx={4}
                            fill={canvasPalette.panel}
                            stroke={canvasPalette.panelBorder}
                            opacity={0.9}
                          />
                          <text
                            x={10}
                            y={14}
                            fontSize="10"
                            fontWeight="bold"
                            fill={canvasPalette.label}
                          >
                            Family Groups
                          </text>
                          {Object.entries(familyGroupColors).map(([group, color], i) => (
                            <g key={group} transform={`translate(10, ${22 + i * 16})`}>
                              <rect width={10} height={10} rx={2} fill={color} />
                              <text x={16} y={9} fontSize="9" fill={canvasPalette.labelMuted}>
                                {group.length > 15 ? group.slice(0, 14) + '…' : group}
                              </text>
                            </g>
                          ))}
                        </g>
                      )}
                    </g>
                  </svg>
                </CanvasPaletteContext.Provider>
              </div>
            </div>
          </div>

          {/* Right panel — Properties / Guests */}
          <CollapsibleSidePanel
            side="right"
            collapsed={panelCollapsed}
            onToggle={() => setWorkspacePref({ panelCollapsed: !panelCollapsed })}
            label={
              activePanel === 'properties'
                ? 'Properties'
                : activePanel === 'chairs'
                  ? 'Chairs'
                  : 'Guests'
            }
            expandedClassName="w-64 p-3"
            testId="side-panel"
            toggleTestId="toggle-side-panel-btn"
          >
            <>
              <SidePanelTabs
                tabs={SIDE_PANEL_TABS}
                active={activePanel}
                onChange={setActivePanel}
              />

              {activePanel === 'chairs' && (
                <ChairTypesPanel items={items} options={chairTypeOptions} />
              )}

              {activePanel === 'properties' && (
                <>
                  {/* ─── Bulk Properties (multi-select) ─────────────── */}
                  {multiSelect.size >= 2 &&
                    (() => {
                      const selectedItems = [...multiSelect]
                        .map((i) => items[i])
                        .filter((it): it is CanvasItem => !!it);
                      const editable = selectedItems.filter((it) => it.objectType !== 'CHAIR');
                      const chairCount = selectedItems.length - editable.length;
                      // Show the shared value when all editable items agree, else blank
                      const common = (get: (it: CanvasItem) => number): number | '' => {
                        const vals = new Set(editable.map(get));
                        return vals.size === 1 ? [...vals][0] : '';
                      };
                      // X/Y align on the visible (rotation-aware) bounding-box edge
                      // so the numbers match the canvas, not the stored anchor.
                      const visualTopLeftFt = (it: CanvasItem) =>
                        anchorToVisualTopLeft(
                          it.x,
                          it.y,
                          ftToPx(it.widthFt),
                          ftToPx(it.heightFt),
                          it.rotation,
                        );
                      return (
                        <div className="mb-4" data-testid="bulk-properties">
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="text-sm font-semibold text-gray-700">
                              {multiSelect.size} items selected
                            </h3>
                            <button
                              onClick={handleDeleteItem}
                              className="p-1 text-red-400 hover:text-red-600"
                              aria-label="Delete selected"
                              data-testid="bulk-delete-btn"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                          <p className="text-xs text-gray-400 mb-2">
                            {editable.length === 0
                              ? 'Chairs are managed by their parent table — only color and delete apply.'
                              : `Changes apply to all selected items${chairCount > 0 ? ' (chairs: color only)' : ''}.`}
                          </p>
                          <div className="space-y-2">
                            {/* Size/position/rotation/seats only make sense when
                            the selection has at least one non-chair item */}
                            {editable.length > 0 && (
                              <>
                                <div className="grid grid-cols-2 gap-2">
                                  <div>
                                    <label
                                      htmlFor="bulk-width-input"
                                      className="block text-xs text-gray-500"
                                    >
                                      Width
                                    </label>
                                    <LengthInput
                                      id="bulk-width-input"
                                      value={common((it) => Math.round(it.widthFt * 24) / 24)}
                                      units={lengthUnits}
                                      min={0.5}
                                      placeholder="mixed"
                                      onChange={(ft) => handleBulkUpdateField('widthFt', ft)}
                                      data-testid="bulk-width-input"
                                    />
                                  </div>
                                  <div>
                                    <label
                                      htmlFor="bulk-height-input"
                                      className="block text-xs text-gray-500"
                                    >
                                      Height
                                    </label>
                                    <LengthInput
                                      id="bulk-height-input"
                                      value={common((it) => Math.round(it.heightFt * 24) / 24)}
                                      units={lengthUnits}
                                      min={0.5}
                                      placeholder="mixed"
                                      onChange={(ft) => handleBulkUpdateField('heightFt', ft)}
                                      data-testid="bulk-height-input"
                                    />
                                  </div>
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                  <div>
                                    <label
                                      htmlFor="bulk-x-input"
                                      className="block text-xs text-gray-500"
                                    >
                                      X — align
                                    </label>
                                    <LengthInput
                                      id="bulk-x-input"
                                      value={common(
                                        (it) => Math.round(pxToFt(visualTopLeftFt(it).x) * 24) / 24,
                                      )}
                                      units={lengthUnits}
                                      min={0}
                                      placeholder="mixed"
                                      onChange={(ft) => handleBulkUpdateField('x', ftToPx(ft))}
                                      data-testid="bulk-x-input"
                                    />
                                  </div>
                                  <div>
                                    <label
                                      htmlFor="bulk-y-input"
                                      className="block text-xs text-gray-500"
                                    >
                                      Y — align
                                    </label>
                                    <LengthInput
                                      id="bulk-y-input"
                                      value={common(
                                        (it) => Math.round(pxToFt(visualTopLeftFt(it).y) * 24) / 24,
                                      )}
                                      units={lengthUnits}
                                      min={0}
                                      placeholder="mixed"
                                      onChange={(ft) => handleBulkUpdateField('y', ftToPx(ft))}
                                      data-testid="bulk-y-input"
                                    />
                                  </div>
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                  <div>
                                    <label className="block text-xs text-gray-500">Rotation</label>
                                    <input
                                      type="number"
                                      value={common((it) => it.rotation)}
                                      onChange={(e) => {
                                        const v = parseInt(e.target.value, 10);
                                        if (!isNaN(v))
                                          handleBulkUpdateField(
                                            'rotation',
                                            ((v % 360) + 360) % 360,
                                          );
                                      }}
                                      min={0}
                                      max={360}
                                      placeholder="mixed"
                                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                      data-testid="bulk-rotation-input"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs text-gray-500">Seats</label>
                                    <input
                                      type="number"
                                      value={common((it) => it.seats)}
                                      onChange={(e) => {
                                        const v = parseInt(e.target.value, 10);
                                        if (!isNaN(v))
                                          handleBulkUpdateField('seats', Math.max(0, v));
                                      }}
                                      min={0}
                                      max={200}
                                      placeholder="mixed"
                                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                      data-testid="bulk-seats-input"
                                    />
                                  </div>
                                </div>
                              </>
                            )}
                            <div>
                              <label className="block text-xs text-gray-500">Color</label>
                              <input
                                type="color"
                                value={editable[0]?.color ?? selectedItems[0]?.color ?? '#e0e0e0'}
                                onChange={(e) => handleBulkUpdateField('color', e.target.value)}
                                className="w-full h-8 px-1 border border-gray-300 rounded cursor-pointer"
                                data-testid="bulk-color-input"
                              />
                            </div>
                            {/* Decor for the whole selection */}
                            {lookEventId && editable.length > 0 && (
                              <ObjectDecorControl
                                items={editable.map(dressableOf)}
                                forWhat={`${editable.length} selected`}
                                planValues={planDecor}
                                planPicks={planPicks}
                                onChange={(decor) =>
                                  handleSetItemsDecor(
                                    [...multiSelect].filter(
                                      (i) => items[i] && items[i].objectType !== 'CHAIR',
                                    ),
                                    decor,
                                  )
                                }
                              />
                            )}
                            {/* Group selected */}
                            <button
                              onClick={handleGroupSelected}
                              className="w-full px-2 py-1.5 bg-purple-50 text-purple-700 border border-purple-200 rounded text-xs hover:bg-purple-100"
                              data-testid="bulk-group-btn"
                            >
                              Group ({multiSelect.size})
                            </button>
                          </div>
                        </div>
                      );
                    })()}

                  {/* ─── Unified Item Properties ────────────────────── */}
                  {multiSelect.size < 2 &&
                    selectedItem &&
                    (() => {
                      const item = selectedItem;
                      const isTable = isTableItem(item);
                      const tableType = isTable
                        ? TABLE_TYPE_REVERSE[item.objectType] ||
                          item.objectType.replace('TABLE_', '')
                        : null;
                      const isRoundOval = tableType === 'ROUND' || tableType === 'OVAL';
                      const isRectType =
                        tableType === 'RECTANGULAR' ||
                        tableType === 'HEAD' ||
                        tableType === 'SQUARE';
                      const canPlaceChairs = isTable || !!item.config?.canHaveChairs;
                      const itemShape = (item.config?.shape as string) || 'RECTANGLE';
                      const isRoundShape =
                        isRoundOval || itemShape === 'CIRCLE' || itemShape === 'OVAL';
                      const serpentine = isSerpentineItem(item);
                      // Seated round its curve only, like a round table: no sides to choose.
                      const halfRound = isHalfRoundItem(item);
                      // The stored x/y are the top-left of the UN-rotated box, but a
                      // rotated non-square item is drawn shifted. Show/edit the
                      // visible bounding-box top-left so the fields match the canvas.
                      const itemWpx = ftToPx(item.widthFt);
                      const itemHpx = ftToPx(item.heightFt);
                      const visualPos = anchorToVisualTopLeft(
                        item.x,
                        item.y,
                        itemWpx,
                        itemHpx,
                        item.rotation,
                      );

                      return (
                        <div
                          className="mb-4"
                          data-testid={isTable ? 'table-details' : 'object-details'}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="text-sm font-semibold text-gray-700">
                              {isTable
                                ? `Table ${item.tableNumber}`
                                : TABLE_SHAPE_LABELS[item.objectType] || item.objectType}
                            </h3>
                            <div className="flex gap-1">
                              <button
                                onClick={handleRotateItem}
                                className="p-1 text-gray-400 hover:text-gray-600"
                                aria-label="Rotate"
                              >
                                <RotateCw size={14} />
                              </button>
                              <button
                                onClick={handleDeleteItem}
                                className="p-1 text-red-400 hover:text-red-600"
                                aria-label="Delete"
                                data-testid="delete-object-btn"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </div>
                          {item.objectType === 'CHAIR' &&
                            (() => {
                              const parentUid = item.config?.[CHAIR_PARENT_FIELD];
                              const parent =
                                typeof parentUid === 'string'
                                  ? itemsByUid.get(parentUid)
                                  : undefined;
                              if (!parent) return null;
                              return (
                                <ChairSeatPanel
                                  seat={seatNumberOf(item)}
                                  parentName={
                                    isTableItem(parent)
                                      ? `Table ${parent.tableNumber}`
                                      : parent.label || 'its table'
                                  }
                                  onRemove={handleDeleteItem}
                                />
                              );
                            })()}
                          <div className="space-y-2">
                            {/* Label */}
                            <div>
                              <label className="block text-xs text-gray-500">Label</label>
                              <input
                                type="text"
                                value={item.label}
                                onChange={(e) => handleUpdateItemField('label', e.target.value)}
                                placeholder={
                                  isTable ? `Table ${item.tableNumber}` : item.objectType
                                }
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                data-testid="table-label-input"
                              />
                            </div>

                            {/* Table Number (tables only) */}
                            {isTable && (
                              <div>
                                <label className="block text-xs text-gray-500">
                                  Table Number
                                  {item.tableNumber === 0 && (
                                    <span className="ml-1 text-amber-500 text-[10px]">
                                      (no seat assignment)
                                    </span>
                                  )}
                                </label>
                                <input
                                  type="number"
                                  value={item.tableNumber!}
                                  onChange={(e) => {
                                    const parsed = parseInt(e.target.value, 10);
                                    if (!isNaN(parsed) && parsed >= 0) {
                                      handleUpdateItemField('tableNumber', parsed);
                                    }
                                  }}
                                  min={0}
                                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                  data-testid="table-number-input"
                                />
                              </div>
                            )}

                            {/* Shape — tables get table-type selector, objects get visual shape selector */}
                            {isTable ? (
                              <div>
                                <label className="block text-xs text-gray-500">Shape</label>
                                <select
                                  value={item.objectType}
                                  onChange={(e) => {
                                    const newObjectType = e.target.value;
                                    const defaults = TABLE_DEFAULTS[newObjectType];
                                    const newColor =
                                      newObjectType === 'TABLE_HEAD'
                                        ? HEAD_TABLE_COLOR
                                        : TABLE_DEFAULT_COLOR;
                                    let newItems = itemsRef.current.map((it, i) => {
                                      if (i !== selectedIdx) return it;
                                      // Sync the visual shape with the new table
                                      // type and drop any custom color override.
                                      const config = { ...it.config };
                                      delete config.useCustomColor;
                                      const visualShape = TABLE_TYPE_VISUAL_SHAPES[newObjectType];
                                      if (visualShape) config.shape = visualShape;
                                      return {
                                        ...it,
                                        objectType: newObjectType,
                                        widthFt: defaults?.widthFt ?? it.widthFt,
                                        heightFt: defaults?.heightFt ?? it.heightFt,
                                        color: newColor,
                                        config,
                                      };
                                    });
                                    // Chairs already placed follow the new outline,
                                    // seat for seat — else a rectangle's chairs stay
                                    // along what is now a half-round's flat edge.
                                    if (selectedIdx != null && canParentChairs(item)) {
                                      newItems = replaceChairsForParents(newItems, [selectedIdx], {
                                        chairSides: chairSides as ChairSides,
                                        defaultChairSizeFt: chairDepthFt,
                                        keepGaps: true,
                                      });
                                      const newIdx = findItemIndex(newItems, item);
                                      if (newIdx >= 0 && newIdx !== selectedIdx)
                                        setSelectedIdx(newIdx);
                                    }
                                    setItems(newItems);
                                    recordChange(newItems);
                                  }}
                                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                  data-testid="table-type-select"
                                >
                                  {Object.entries(TABLE_SHAPE_LABELS).map(([val, label]) => (
                                    <option key={val} value={val}>
                                      {label}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            ) : (
                              <div>
                                <label className="block text-xs text-gray-500">Shape</label>
                                <select
                                  value={(item.config?.shape as string) || 'RECTANGLE'}
                                  onChange={(e) => {
                                    const newShape = e.target.value;
                                    const newItems = itemsRef.current.map((it, i) =>
                                      i === selectedIdx
                                        ? {
                                            ...it,
                                            config: { ...it.config, shape: newShape },
                                          }
                                        : it,
                                    );
                                    setItems(newItems);
                                    recordChange(newItems);
                                  }}
                                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                  data-testid="object-shape-select"
                                >
                                  <option value="RECTANGLE">Rectangle</option>
                                  <option value="CIRCLE">Circle</option>
                                  <option value="OVAL">Oval</option>
                                  <option value="ROUNDED_RECT">Rounded Rect</option>
                                  <option value="DIAMOND">Diamond</option>
                                  <option value="HEXAGON">Hexagon</option>
                                  <option value="SERPENTINE">Serpentine</option>
                                  <option value="HALF_ROUND">Half-round</option>
                                </select>
                              </div>
                            )}

                            {/* Seats */}
                            <SeatCountField
                              seats={item.seats}
                              onChange={(n) => handleUpdateItemField('seats', n)}
                            />
                            {canParentChairs(item) &&
                              (() => {
                                // Measured with the chairs it has — their size and the sides
                                // they are on — else what new ones would get. Chair Sides is
                                // one setting for the page, so another table's choice must
                                // not be read as this one's.
                                const placed = chairsOf(items, item);
                                const capacity = tableSeatCapacity(item, {
                                  chairSizeFt: placed[0]?.widthFt ?? chairDepthFt,
                                  chairSides: tableChairSides(
                                    item,
                                    chairSides as ChairSides,
                                    placed,
                                  ),
                                });
                                return (
                                  <SeatCapacityWarning
                                    seats={capacity.seats}
                                    fits={capacity.fits}
                                    reason={capacity.reason}
                                    onFit={(n) => handleUpdateItemField('seats', n)}
                                  />
                                );
                              })()}

                            {/* Serpentine: radius / depth / arc instead of a free box */}
                            {serpentine && (
                              <SerpentineControls
                                item={item}
                                showChairSides={canPlaceChairs}
                                onChange={handleUpdateSerpentine}
                              />
                            )}

                            {/* Width / Height */}
                            {!serpentine && (
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <label
                                    htmlFor="object-width-input"
                                    className="block text-xs text-gray-500"
                                  >
                                    Width
                                  </label>
                                  <LengthInput
                                    id="object-width-input"
                                    value={item.widthFt}
                                    units={lengthUnits}
                                    min={0.5}
                                    onChange={(ft) => handleUpdateItemField('widthFt', ft)}
                                    data-testid="object-width-input"
                                  />
                                </div>
                                <div>
                                  <label
                                    htmlFor="object-height-input"
                                    className="block text-xs text-gray-500"
                                  >
                                    Height
                                  </label>
                                  <LengthInput
                                    id="object-height-input"
                                    value={item.heightFt}
                                    units={lengthUnits}
                                    min={0.5}
                                    onChange={(ft) => handleUpdateItemField('heightFt', ft)}
                                    data-testid="object-height-input"
                                  />
                                </div>
                              </div>
                            )}

                            {/* Position X / Y */}
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <label
                                  htmlFor="object-x-input"
                                  className="block text-xs text-gray-500"
                                >
                                  X
                                </label>
                                <LengthInput
                                  id="object-x-input"
                                  value={pxToFt(visualPos.x)}
                                  units={lengthUnits}
                                  min={0}
                                  onChange={(ft) => {
                                    // Clamp the entered VISIBLE edge (not the anchor)
                                    // to the canvas using the rotation-aware box
                                    // width, then convert back to the stored anchor.
                                    // The move path floors on the visible box, so a
                                    // visible edge of 0 lands the item flush against
                                    // the left wall (its stored anchor goes negative).
                                    const box = rotatedAabbSize(itemWpx, itemHpx, item.rotation);
                                    const maxVisualX = Math.max(0, canvasWidthPx - box.width);
                                    const visualX = Math.max(0, Math.min(ftToPx(ft), maxVisualX));
                                    const anchor = visualTopLeftToAnchor(
                                      visualX,
                                      visualPos.y,
                                      itemWpx,
                                      itemHpx,
                                      item.rotation,
                                    );
                                    handleUpdateItemField('x', anchor.x);
                                  }}
                                  data-testid="object-x-input"
                                />
                              </div>
                              <div>
                                <label
                                  htmlFor="object-y-input"
                                  className="block text-xs text-gray-500"
                                >
                                  Y
                                </label>
                                <LengthInput
                                  id="object-y-input"
                                  value={pxToFt(visualPos.y)}
                                  units={lengthUnits}
                                  min={0}
                                  onChange={(ft) => {
                                    // Clamp the entered VISIBLE edge (not the anchor)
                                    // to the canvas using the rotation-aware box
                                    // height, then convert back to the stored anchor.
                                    // The move path floors on the visible box, so a
                                    // visible edge of 0 lands the item flush against
                                    // the top wall (its stored anchor goes negative).
                                    const box = rotatedAabbSize(itemWpx, itemHpx, item.rotation);
                                    const maxVisualY = Math.max(0, canvasHeightPx - box.height);
                                    const visualY = Math.max(0, Math.min(ftToPx(ft), maxVisualY));
                                    const anchor = visualTopLeftToAnchor(
                                      visualPos.x,
                                      visualY,
                                      itemWpx,
                                      itemHpx,
                                      item.rotation,
                                    );
                                    handleUpdateItemField('y', anchor.y);
                                  }}
                                  data-testid="object-y-input"
                                />
                              </div>
                            </div>

                            {/* Rotation */}
                            <div>
                              <label className="block text-xs text-gray-500">Rotation</label>
                              <input
                                type="number"
                                value={item.rotation}
                                onChange={(e) =>
                                  handleUpdateItemField('rotation', parseInt(e.target.value) || 0)
                                }
                                min={0}
                                max={360}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                              />
                            </div>

                            {/* Color */}
                            <div>
                              <label className="block text-xs text-gray-500">Color</label>
                              <input
                                type="color"
                                value={item.color}
                                onChange={(e) => handleUpdateItemField('color', e.target.value)}
                                className="w-full h-8 px-1 border border-gray-300 rounded cursor-pointer"
                                data-testid="object-color-input"
                              />
                            </div>

                            {/* Opacity (for AREA type) */}
                            {item.objectType === 'AREA' && (
                              <div>
                                <label className="block text-xs text-gray-500">
                                  Opacity ({Math.round(item.opacity * 100)}%)
                                </label>
                                <input
                                  type="range"
                                  min="0"
                                  max="1"
                                  step="0.05"
                                  value={item.opacity}
                                  onChange={(e) =>
                                    handleUpdateItemField('opacity', parseFloat(e.target.value))
                                  }
                                  className="w-full"
                                  data-testid="object-opacity-input"
                                />
                              </div>
                            )}

                            {/* Group assignment */}
                            <div>
                              <label className="block text-xs text-gray-500">
                                <Palette size={10} className="inline mr-1" />
                                Group
                              </label>
                              <select
                                value={
                                  isTable && item.tableNumber
                                    ? tableGroups[item.tableNumber] || ''
                                    : ''
                                }
                                onChange={(e) =>
                                  handleSetTableGroup(e.target.value as TableGroup | '')
                                }
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                data-testid="table-group-select"
                              >
                                <option value="">None</option>
                                <option value="bride">Bride&apos;s Side</option>
                                <option value="groom">Groom&apos;s Side</option>
                                <option value="vip">VIP</option>
                                <option value="family">Family</option>
                                <option value="general">General</option>
                              </select>
                            </div>

                            {/* Status info */}
                            <div className="text-xs text-gray-500">
                              {TABLE_SHAPE_LABELS[item.objectType] || item.objectType}
                              {isTable
                                ? ` | Seated: ${seatedPerTable[item.tableNumber!] || 0}/${item.seats}`
                                : item.seats > 0
                                  ? ` · ${item.seats} seats`
                                  : ''}
                            </div>

                            {/* Chair side selector (rectangular/square/head items) */}
                            {canPlaceChairs &&
                              !serpentine &&
                              !halfRound &&
                              (isRectType || !isRoundShape) && (
                                <div>
                                  <label className="block text-xs text-gray-500">Chair Sides</label>
                                  <select
                                    value={tableChairSides(
                                      item,
                                      chairSides as ChairSides,
                                      chairsOf(items, item),
                                    )}
                                    onChange={(e) => {
                                      const newSides = e.target.value;
                                      setChairSides(newSides);
                                      replaceChairsForSelected(
                                        chairDepthFt,
                                        newSides as ChairSides,
                                      );
                                    }}
                                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                    data-testid="chair-sides-select"
                                  >
                                    <option value="top-bottom">Top + Bottom</option>
                                    <option value="left-right">Left + Right</option>
                                    <option value="all">All Sides</option>
                                    <option value="top">Top Only</option>
                                    <option value="bottom">Bottom Only</option>
                                    <option value="left">Left Only</option>
                                    <option value="right">Right Only</option>
                                  </select>
                                </div>
                              )}

                            {/* Chair size input (items with chairs) */}
                            {canPlaceChairs && (
                              <div>
                                <label className="block text-xs text-gray-500">
                                  Chair size (ft)
                                </label>
                                <input
                                  type="number"
                                  value={chairDepthFt}
                                  onChange={(e) => {
                                    const newDepth = Math.max(
                                      0.5,
                                      parseFloat(e.target.value) || 1.5,
                                    );
                                    setChairDepthFt(newDepth);
                                    replaceChairsForSelected(newDepth);
                                  }}
                                  min={0.5}
                                  max={4}
                                  step={0.5}
                                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                />
                              </div>
                            )}

                            {/* Chair type — what the rental order counts */}
                            {takesChairType(item) && (
                              <ChairTypeSelect
                                options={chairTypeOptions}
                                value={chairTypeOf(item)}
                                onChange={(chairType) => {
                                  const config = { ...(item.config ?? {}) };
                                  if (chairType) config.chairType = chairType;
                                  else delete config.chairType;
                                  handleUpdateItemField('config', config);
                                }}
                              />
                            )}

                            {/* Which side of the mechitza the table is on */}
                            {isTableItem(item) && (
                              <SeatingSectionControl
                                value={tableSectionOf(item.config)}
                                onChange={(section) => {
                                  const config = { ...(item.config ?? {}) };
                                  if (section) config.section = section;
                                  else delete config.section;
                                  handleUpdateItemField('config', config);
                                }}
                              />
                            )}

                            {/* Decor from the library — this object's own, or the plan's */}
                            {lookEventId &&
                              (() => {
                                const likeThis = items
                                  .map((it, i) => (it.objectType === item.objectType ? i : -1))
                                  .filter((i) => i >= 0);
                                return (
                                  <ObjectDecorControl
                                    items={[dressableOf(item)]}
                                    forWhat={itemDecorName(item)}
                                    planValues={planDecor}
                                    planPicks={planPicks}
                                    onChange={(decor) =>
                                      selectedIdx !== null &&
                                      handleSetItemsDecor([selectedIdx], decor)
                                    }
                                    likeThisCount={likeThis.length}
                                    onApplyToAllLikeThis={() => {
                                      const own = readDecorLook(item.config?.decor ?? null);
                                      handleSetItemsDecor(likeThis, () => own);
                                    }}
                                  />
                                );
                              })()}

                            {/* Place / Remove Chairs button */}
                            {canPlaceChairs &&
                              (() => {
                                const chairCount = chairsOf(items, item).length;
                                const hasChairs = chairCount > 0;
                                return (
                                  <button
                                    onClick={handlePlaceChairs}
                                    className={`w-full flex items-center justify-center gap-1 px-2 py-1.5 border rounded text-xs ${
                                      hasChairs
                                        ? 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100'
                                        : 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100'
                                    }`}
                                    data-testid="place-chairs-btn"
                                  >
                                    <Armchair size={12} />
                                    {hasChairs
                                      ? `Remove Chairs (${chairCount})`
                                      : `Place Chairs (${item.seats})`}
                                  </button>
                                );
                              })()}

                            {/* Clone button */}
                            <button
                              onClick={handleCloneItem}
                              className="w-full flex items-center justify-center gap-1 px-2 py-1.5 border border-gray-200 rounded text-xs hover:bg-gray-50"
                              data-testid="clone-table-btn"
                            >
                              <Copy size={12} />
                              Clone {isTable ? 'Table' : 'Object'}
                            </button>

                            {/* Group / Ungroup */}
                            <div className="border border-gray-200 rounded p-2 space-y-1">
                              <label className="block text-xs text-gray-500">Group</label>
                              {item.groupId && (
                                <div className="text-[10px] text-gray-400 truncate">
                                  {items.filter((it) => it.groupId === item.groupId).length} items
                                  grouped
                                </div>
                              )}
                              <div className="flex gap-1">
                                {multiSelect.size >= 2 && (
                                  <button
                                    onClick={handleGroupSelected}
                                    className="flex-1 px-2 py-1 bg-purple-50 text-purple-700 border border-purple-200 rounded text-xs hover:bg-purple-100"
                                    data-testid="group-selected-btn"
                                  >
                                    Group ({multiSelect.size})
                                  </button>
                                )}
                                {item.groupId && (
                                  <button
                                    onClick={handleUngroupSelected}
                                    className="flex-1 px-2 py-1 bg-orange-50 text-orange-700 border border-orange-200 rounded text-xs hover:bg-orange-100"
                                    data-testid="ungroup-btn"
                                  >
                                    Ungroup
                                  </button>
                                )}
                              </div>
                              {!item.groupId && multiSelect.size < 2 && (
                                <div className="text-[10px] text-gray-400">
                                  Shift+click or drag-select items, then click Group.
                                </div>
                              )}
                            </div>

                            {/* Directional clone (all items) */}
                            <div className="border border-gray-200 rounded p-2 space-y-1">
                              <label className="block text-xs text-gray-500">Clone Multiple</label>
                              <div className="flex gap-1">
                                <input
                                  type="number"
                                  value={cloneCount}
                                  onChange={(e) =>
                                    setCloneCount(
                                      Math.max(1, Math.min(10, parseInt(e.target.value) || 1)),
                                    )
                                  }
                                  min={1}
                                  max={10}
                                  className="w-12 px-1 py-1 border border-gray-300 rounded text-xs"
                                  data-testid="clone-count-input"
                                />
                                <select
                                  value={cloneDirection}
                                  onChange={(e) =>
                                    setCloneDirection(e.target.value as 'right' | 'below')
                                  }
                                  className="flex-1 px-1 py-1 border border-gray-300 rounded text-xs"
                                  data-testid="clone-direction-select"
                                >
                                  <option value="right">&rarr; Right</option>
                                  <option value="below">&darr; Below</option>
                                </select>
                              </div>
                              <div className="flex gap-1 items-center">
                                <label className="text-xs text-gray-400 whitespace-nowrap">
                                  Gap
                                </label>
                                <input
                                  type="number"
                                  value={cloneSpacingFt}
                                  onChange={(e) =>
                                    setCloneSpacingFt(Math.max(0, parseFloat(e.target.value) || 0))
                                  }
                                  min={0}
                                  step={0.5}
                                  className="w-12 px-1 py-1 border border-gray-300 rounded text-xs"
                                  data-testid="clone-spacing-input"
                                />
                                <span className="text-xs text-gray-400">ft</span>
                                <button
                                  onClick={handleDirectionalClone}
                                  className="flex-1 px-2 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded text-xs hover:bg-blue-100"
                                  data-testid="directional-clone-btn"
                                >
                                  Clone {cloneCount}&times;
                                </button>
                              </div>
                            </div>

                            {/* Group info — moved to Group/Ungroup section above */}

                            {/* Quick Rotate */}
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">
                                Quick Rotate
                              </label>
                              <div className="flex gap-1">
                                {[45, 90, -90, 180].map((deg) => (
                                  <button
                                    key={deg}
                                    onClick={() => handleRotateItemBy(deg)}
                                    className="flex-1 px-1 py-1 border border-gray-200 rounded text-xs hover:bg-gray-50"
                                    title={`Rotate ${deg}°`}
                                  >
                                    {deg > 0 ? `+${deg}` : deg}&deg;
                                  </button>
                                ))}
                              </div>
                            </div>

                            {/* Nudge */}
                            <div>
                              <label
                                className="block text-xs text-gray-500 mb-1"
                                data-testid="nudge-label"
                              >
                                Nudge ({formatLength(snapEnabled ? gridSizeFt : 1, lengthUnits)})
                              </label>
                              <div className="grid grid-cols-3 gap-0.5 w-20 mx-auto">
                                <div />
                                <button
                                  onClick={() => handleNudgeItem(0, -1)}
                                  className="p-1 border border-gray-200 rounded text-xs hover:bg-gray-50 flex justify-center"
                                  aria-label="Nudge up"
                                >
                                  <ChevronUp size={12} />
                                </button>
                                <div />
                                <button
                                  onClick={() => handleNudgeItem(-1, 0)}
                                  className="p-1 border border-gray-200 rounded text-xs hover:bg-gray-50 flex justify-center"
                                  aria-label="Nudge left"
                                >
                                  <ChevronLeft size={12} />
                                </button>
                                <div className="p-1 flex justify-center items-center">
                                  <Hash size={10} className="text-gray-300" />
                                </div>
                                <button
                                  onClick={() => handleNudgeItem(1, 0)}
                                  className="p-1 border border-gray-200 rounded text-xs hover:bg-gray-50 flex justify-center"
                                  aria-label="Nudge right"
                                >
                                  <ChevronRight size={12} />
                                </button>
                                <div />
                                <button
                                  onClick={() => handleNudgeItem(0, 1)}
                                  className="p-1 border border-gray-200 rounded text-xs hover:bg-gray-50 flex justify-center"
                                  aria-label="Nudge down"
                                >
                                  <ChevronDown size={12} />
                                </button>
                                <div />
                              </div>
                            </div>

                            {/* Footprint (all items) */}
                            {(() => {
                              const fp = calculateTableFootprint(
                                item.widthFt,
                                item.heightFt,
                                isRoundOval ?? false,
                                item.seats,
                              );
                              return (
                                <div
                                  className="text-xs text-gray-400"
                                  data-testid="table-footprint"
                                >
                                  Footprint: {formatLength(fp.widthFt, lengthUnits)} x{' '}
                                  {formatLength(fp.heightFt, lengthUnits)}
                                </div>
                              );
                            })()}

                            {/* Per-table meal counts (tables only) */}
                            {isTable && mealCounts[item.tableNumber!] && (
                              <div data-testid="meal-counts">
                                <h4 className="text-xs font-medium text-gray-600 mt-1">
                                  Meal Counts
                                </h4>
                                <ul className="text-xs text-gray-500 space-y-0.5">
                                  {Object.entries(mealCounts[item.tableNumber!]).map(
                                    ([meal, count]) => (
                                      <li key={meal}>
                                        {count} {meal}
                                      </li>
                                    ),
                                  )}
                                </ul>
                              </div>
                            )}

                            {/* Assigned guests with seat-level assignment (tables only) */}
                            {isTable && assignments[item.tableNumber!] && (
                              <div>
                                <h4 className="text-xs font-medium text-gray-600 mt-2">
                                  Assigned Guests (Seats)
                                </h4>
                                <ul className="text-xs text-gray-600 space-y-0.5">
                                  {assignments[item.tableNumber!].map(
                                    (g: {
                                      id: string;
                                      firstName: string;
                                      lastName: string | null;
                                      seatNumber: number | null;
                                    }) => (
                                      <li key={g.id} className="flex items-center justify-between">
                                        <span>
                                          {g.seatNumber ? `S${g.seatNumber}: ` : ''}
                                          {g.firstName} {g.lastName || ''}
                                        </span>
                                      </li>
                                    ),
                                  )}
                                </ul>
                              </div>
                            )}

                            {/* Seat assignment from unassigned (tables only) */}
                            {isTable && eventId && (
                              <div className="mt-2">
                                <h4 className="text-xs font-medium text-gray-600">
                                  Assign to Seat
                                </h4>
                                <div className="flex gap-1 flex-wrap mt-1">
                                  {Array.from({ length: item.seats }, (_, i) => i + 1).map(
                                    (seatNum) => {
                                      const assigned = assignments[item.tableNumber!]?.find(
                                        (g: { seatNumber: number | null }) =>
                                          g.seatNumber === seatNum,
                                      );
                                      return (
                                        <span
                                          key={seatNum}
                                          className={`inline-block w-6 h-6 text-center leading-6 text-[10px] rounded border ${
                                            assigned
                                              ? 'bg-green-100 border-green-300 text-green-700'
                                              : 'bg-gray-50 border-gray-200 text-gray-400'
                                          }`}
                                          title={
                                            assigned
                                              ? `${assigned.firstName} ${assigned.lastName || ''}`
                                              : `Seat ${seatNum} (empty)`
                                          }
                                        >
                                          {seatNum}
                                        </span>
                                      );
                                    },
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Chair row config (CHAIR_ROW objects) */}
                            {item.objectType === 'CHAIR_ROW' && item.config && (
                              <div className="space-y-1">
                                <h4 className="text-xs font-medium text-gray-600">
                                  Chair Row Config
                                </h4>
                                <div className="grid grid-cols-2 gap-1">
                                  <div>
                                    <label className="block text-[10px] text-gray-400">Rows</label>
                                    <input
                                      type="number"
                                      value={(item.config.rows as number) || 1}
                                      onChange={(e) => {
                                        const rows = parseInt(e.target.value) || 1;
                                        const cfg = item.config;
                                        const config = { ...cfg, rows };
                                        const group = calculateChairRowGroup(
                                          rows,
                                          (cfg.seatsPerRow as number) || 10,
                                          (cfg.chairSpacingFt as number) || 2,
                                          (cfg.rowSpacingFt as number) || 3,
                                        );
                                        // Batch update all fields
                                        const newItems = itemsRef.current.map((it, i) =>
                                          i === selectedIdx
                                            ? {
                                                ...it,
                                                config,
                                                widthFt: group.totalWidthFt,
                                                heightFt: group.totalHeightFt,
                                                seats: rows * ((cfg.seatsPerRow as number) || 10),
                                              }
                                            : it,
                                        );
                                        setItems(newItems);
                                        recordChange(newItems);
                                      }}
                                      min={1}
                                      max={20}
                                      className="w-full px-1 py-0.5 border border-gray-300 rounded text-xs"
                                      data-testid="chair-row-rows"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-[10px] text-gray-400">
                                      Seats/Row
                                    </label>
                                    <input
                                      type="number"
                                      value={(item.config.seatsPerRow as number) || 10}
                                      onChange={(e) => {
                                        const seatsPerRow = parseInt(e.target.value) || 1;
                                        const cfg = item.config;
                                        const config = { ...cfg, seatsPerRow };
                                        const group = calculateChairRowGroup(
                                          (cfg.rows as number) || 1,
                                          seatsPerRow,
                                          (cfg.chairSpacingFt as number) || 2,
                                          (cfg.rowSpacingFt as number) || 3,
                                        );
                                        const newItems = itemsRef.current.map((it, i) =>
                                          i === selectedIdx
                                            ? {
                                                ...it,
                                                config,
                                                widthFt: group.totalWidthFt,
                                                heightFt: group.totalHeightFt,
                                                seats: ((cfg.rows as number) || 1) * seatsPerRow,
                                              }
                                            : it,
                                        );
                                        setItems(newItems);
                                        recordChange(newItems);
                                      }}
                                      min={1}
                                      max={30}
                                      className="w-full px-1 py-0.5 border border-gray-300 rounded text-xs"
                                      data-testid="chair-row-seats"
                                    />
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })()}

                  {/* No selection — room/plan info */}
                  {!selectedItem && (
                    <div className="text-sm text-gray-500">
                      {selectedRoom ? (
                        <div className="space-y-2">
                          <h3 className="font-semibold text-gray-700">Room: {selectedRoom.name}</h3>
                          {selectedRoom.roomType && (
                            <span
                              className="inline-block px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs"
                              data-testid="room-type-badge"
                            >
                              {selectedRoom.roomType}
                            </span>
                          )}
                          <p>
                            {formatLength(selectedRoom.widthFt, lengthUnits)} x{' '}
                            {formatLength(selectedRoom.heightFt, lengthUnits)}
                          </p>
                          <RoomCapacitySummary
                            banquet={selectedRoom.maxCapacity}
                            cocktail={selectedRoom.cocktailCapacity}
                            seats={totalSeats}
                          />
                          {roomMeasurements && roomShape && (
                            <div className="text-xs space-y-0.5" data-testid="room-shape-summary">
                              <p>
                                {roomMeasurements.areaSqFt.toLocaleString()} sq ft
                                {roomMeasurements.usableAreaSqFt !== roomMeasurements.areaSqFt &&
                                  ` · ${roomMeasurements.usableAreaSqFt.toLocaleString()} usable`}
                              </p>
                              <p>
                                {roomMeasurements.walls} walls
                                {roomShape.openings.length > 0 &&
                                  ` · ${roomShape.openings.length} doors & windows`}
                                {roomShape.features.length > 0 &&
                                  ` · ${roomShape.features.length} fixed features`}
                              </p>
                            </div>
                          )}
                          {!roomShape &&
                            selectedRoom.perimeterPoints &&
                            selectedRoom.perimeterPoints.length >= 3 && (
                              <div>
                                <span className="text-xs px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">
                                  Custom Perimeter ({selectedRoom.perimeterPoints.length} points)
                                </span>
                              </div>
                            )}
                          {selectedRoom.entries?.length > 0 && (
                            <div>
                              <h4 className="text-xs font-medium text-gray-600">Entries</h4>
                              <ul className="text-xs space-y-0.5">
                                {selectedRoom.entries.map((e) => (
                                  <li key={e.id}>
                                    {e.name} ({e.wall}, {Math.round(e.position * 100)}%)
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      ) : (
                        <p>
                          Select an object to edit its properties, or drag from the library to add.
                        </p>
                      )}
                    </div>
                  )}
                </>
              )}

              {activePanel === 'library' && (
                <>
                  {eventId && (
                    <div>
                      {/* Assigned guests for selected table (hidden for table number 0) */}
                      {selectedItem &&
                        isTableItem(selectedItem) &&
                        selectedItem.tableNumber !== 0 &&
                        (() => {
                          const tableNum = selectedItem.tableNumber!;
                          const assignedGuests = guests.filter(
                            (g: Guest) => g.tableNumber === tableNum,
                          );
                          if (assignedGuests.length === 0) return null;
                          return (
                            <div className="mb-3">
                              <h3 className="text-xs font-semibold text-gray-600 mb-1 flex items-center gap-1">
                                <Users size={12} />
                                Table {tableNum} ({assignedGuests.length} guests ·{' '}
                                {assignedGuests.reduce(
                                  (s, g) =>
                                    s +
                                    (g.maleCount ?? 0) +
                                    (g.femaleCount ?? 0) +
                                    (g.kidCount ?? 0),
                                  0,
                                )}{' '}
                                people)
                              </h3>
                              <ul
                                className="space-y-0.5 max-h-36 overflow-auto"
                                data-testid="assigned-list"
                              >
                                {assignedGuests
                                  .slice()
                                  .sort((a, b) => (a.seatNumber ?? 999) - (b.seatNumber ?? 999))
                                  .map((g: Guest) => (
                                    <li
                                      key={g.id}
                                      className="flex items-center justify-between text-xs text-gray-700 py-0.5 px-1 bg-green-50 rounded"
                                    >
                                      <span>
                                        {g.seatNumber ? (
                                          <span className="font-medium text-green-700 mr-1">
                                            S{g.seatNumber}
                                          </span>
                                        ) : null}
                                        {g.firstName} {g.lastName || ''}
                                        {g.familyGroup ? (
                                          <span className="ml-1 text-gray-400">
                                            ({g.familyGroup})
                                          </span>
                                        ) : null}
                                      </span>
                                      <span className="text-gray-400 text-[10px]">
                                        {(g.maleCount ?? 0) +
                                          (g.femaleCount ?? 0) +
                                          (g.kidCount ?? 0)}
                                        p
                                      </span>
                                    </li>
                                  ))}
                              </ul>
                              <div className="border-t border-gray-100 mt-2" />
                            </div>
                          );
                        })()}

                      {/* Unassigned guests */}
                      <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1">
                        <Users size={14} />
                        Unassigned ({unassignedGuests.length})
                      </h3>
                      <input
                        type="text"
                        value={guestSearchTerm}
                        onChange={(e) => setGuestSearchTerm(e.target.value)}
                        placeholder="Search guests..."
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm mb-2"
                        data-testid="guest-search"
                      />
                      <ul
                        className="space-y-1 max-h-48 overflow-auto"
                        data-testid="unassigned-list"
                      >
                        {filteredUnassigned.map((g: Guest) => (
                          <li
                            key={g.id}
                            className="flex items-center justify-between text-xs text-gray-600 py-1 px-1 hover:bg-gray-50 rounded"
                          >
                            <span>
                              {g.firstName} {g.lastName || ''}
                              {g.familyGroup ? (
                                <span className="ml-1 text-gray-400 text-[10px]">
                                  ({g.familyGroup})
                                </span>
                              ) : null}
                            </span>
                            {selectedItem &&
                              isTableItem(selectedItem) &&
                              selectedItem.tableNumber !== 0 && (
                                <div className="flex items-center gap-1">
                                  <button
                                    onClick={() => handleAssignGuest(g.id)}
                                    className="text-primary-600 hover:text-primary-700 text-xs"
                                    data-testid={`assign-${g.id}`}
                                  >
                                    Assign
                                  </button>
                                  <select
                                    onChange={(e) => {
                                      const seatNum = parseInt(e.target.value);
                                      if (seatNum > 0) handleAssignSeat(g.id, seatNum);
                                    }}
                                    defaultValue=""
                                    className="text-[10px] border border-gray-200 rounded px-0.5"
                                    data-testid={`seat-select-${g.id}`}
                                  >
                                    <option value="">Seat</option>
                                    {Array.from({ length: selectedItem.seats }, (_, i) => (
                                      <option key={i + 1} value={i + 1}>
                                        S{i + 1}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              )}
                          </li>
                        ))}
                        {filteredUnassigned.length === 0 && (
                          <li className="text-xs text-gray-400 py-1">
                            {unassignedGuests.length === 0 ? 'All guests assigned' : 'No match'}
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </>
          </CollapsibleSidePanel>
        </div>
      </SeatingDnd>

      {selectedRoom && (
        <RoomShapeEditorModal
          open={showShapeEditor}
          onClose={() => setShowShapeEditor(false)}
          room={selectedRoom}
          onSaved={handleRoomSaved}
        />
      )}

      {/* Seating modals (deterministic + AI) */}
      {selectedPlanId && (
        <>
          <AiSeatingModal
            open={showAiSeatingModal}
            onClose={() => setShowAiSeatingModal(false)}
            floorPlanId={selectedPlanId}
          />
          {showAutoAssignModal && (
            <AutoAssignSeatsModal
              open
              onClose={() => setShowAutoAssignModal(false)}
              floorPlanId={selectedPlanId}
              hasSections={tableItems.some((item) => tableSectionOf(item.config) !== null)}
            />
          )}
        </>
      )}

      <DuplicatePlanModal
        open={showDuplicateModal}
        onClose={() => setShowDuplicateModal(false)}
        onDuplicate={handleDuplicate}
        isPending={duplicateFloorPlan.isPending}
      />

      {partModal === 'edit' ? (
        <PlanPartModal
          mode="edit"
          open
          onClose={() => setPartModal(null)}
          parts={eveningParts}
          initialPart={loadedPlan?.eveningPart ?? null}
          initialTime={loadedPlan?.startTime ?? null}
          isSaving={updateFloorPlanMutation.isPending}
          onSave={handleSavePlanPart}
        />
      ) : partModal === 'new' ? (
        <PlanPartModal
          mode="new"
          open
          onClose={() => setPartModal(null)}
          parts={eveningParts}
          initialPart={suggestedPart?.value ?? null}
          initialTime={null}
          // Only once the canvas holds the plan on screen: a plan still
          // loading would copy the one shown before it.
          canCopy={!!selectedPlanId && loadedPlan?.id === selectedPlanId}
          isSaving={createFloorPlan.isPending}
          onSave={handleNewPartPlan}
        />
      ) : null}

      <SaveAsPlanModal
        open={showSaveAsModal}
        onClose={() => setShowSaveAsModal(false)}
        initialName={saveAsName}
        initialEventId={saveAsEventId}
        isSaving={createFloorPlan.isPending}
        onSave={handleSaveAs}
      />

      {/* Bottom bar */}
      <div
        className={`flex flex-wrap items-center justify-between gap-x-4 gap-y-2 bg-white rounded-lg shadow px-4 py-2 text-xs text-gray-600 ${focusMode ? 'flex-shrink-0' : 'mt-3'}`}
        data-testid="bottom-bar"
      >
        {/* Wraps rather than running off a narrow screen, where focus mode
            hides the overflow and a control past the edge is unreachable. */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
          {/* Undo / Redo */}
          <div className="flex items-center gap-0.5">
            <button
              onClick={handleUndo}
              disabled={!canUndo}
              className="p-1 hover:bg-gray-100 rounded disabled:opacity-30"
              data-testid="undo-btn"
              aria-label="Undo"
              title="Undo (Ctrl+Z)"
            >
              <Undo2 size={14} />
            </button>
            <button
              onClick={handleRedo}
              disabled={!canRedo}
              className="p-1 hover:bg-gray-100 rounded disabled:opacity-30"
              data-testid="redo-btn"
              aria-label="Redo"
              title="Redo (Ctrl+Y)"
            >
              <Redo2 size={14} />
            </button>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={handleZoomOut}
              className="p-1 hover:bg-gray-100 rounded"
              data-testid="zoom-out-btn"
              aria-label="Zoom out"
            >
              <ZoomOut size={14} />
            </button>
            <span className="flex items-center" data-testid="zoom-level">
              <input
                type="number"
                value={zoomInput}
                onChange={(e) => setZoomInput(e.target.value)}
                onBlur={commitZoomInput}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitZoomInput();
                }}
                min={20}
                max={300}
                step={5}
                className="w-14 px-1 py-0.5 border border-gray-200 rounded text-xs text-right"
                aria-label="Zoom percentage"
                data-testid="zoom-input"
              />
              %
            </span>
            <button
              onClick={handleZoomIn}
              className="p-1 hover:bg-gray-100 rounded"
              data-testid="zoom-in-btn"
              aria-label="Zoom in"
            >
              <ZoomIn size={14} />
            </button>
          </div>
          {/* Canvas rotation */}
          <div className="flex items-center gap-0.5 border-l border-gray-200 pl-2">
            <button
              onClick={() => setCanvasRotation((r) => ((r - 90 + 360) % 360) as 0 | 90 | 180 | 270)}
              className="p-1 hover:bg-gray-100 rounded"
              title="Rotate canvas 90° counter-clockwise"
              aria-label="Rotate CCW"
              data-testid="canvas-rotate-ccw"
            >
              <RotateCcw size={14} />
            </button>
            <span className="text-xs min-w-[28px] text-center" data-testid="canvas-rotation">
              {canvasRotation}°
            </span>
            <button
              onClick={() => setCanvasRotation((r) => ((r + 90) % 360) as 0 | 90 | 180 | 270)}
              className="p-1 hover:bg-gray-100 rounded"
              title="Rotate canvas 90° clockwise"
              aria-label="Rotate CW"
              data-testid="canvas-rotate-cw"
            >
              <RotateCw size={14} />
            </button>
          </div>
          <button
            onClick={() => setShowGrid(!showGrid)}
            className={`flex items-center gap-1 px-2 py-1 rounded ${showGrid ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}`}
            data-testid="grid-toggle"
          >
            <Grid3X3 size={12} />
            Grid
          </button>
          <button
            onClick={() => setSnapEnabled(!snapEnabled)}
            className={`flex items-center gap-1 px-2 py-1 rounded ${snapEnabled ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}`}
            data-testid="snap-toggle"
          >
            <Magnet size={12} />
            Snap
          </button>
          <GridSizeControl
            size={gridSizeFt}
            units={lengthUnits}
            onChange={handleGridSizeChange}
            disabled={!gridReady}
          />
          <button
            onClick={() => {
              setMeasureMode(!measureMode);
              setMeasurePoints([]);
            }}
            className={`flex items-center gap-1 px-2 py-1 rounded ${measureMode ? 'bg-red-100 text-red-700' : 'hover:bg-gray-100'}`}
            data-testid="measure-toggle"
          >
            <RulerIcon size={12} />
            Measure
          </button>
          <LengthUnitsToggle
            units={lengthUnits}
            onChange={(units) => setWorkspacePref({ lengthUnits: units })}
          />
          <CanvasThemeToggle
            choice={canvasTheme}
            onChange={(choice) => setWorkspacePref({ canvasTheme: choice })}
          />
          <SpacingControl
            issues={spacingIssues}
            clearanceFt={clearanceFt}
            nameOf={spacingNameOf}
            showOnPlan={showSpacing}
            onShowOnPlanChange={(v) => setWorkspacePref({ showSpacing: v })}
            preventOverlap={preventOverlap}
            onPreventOverlapChange={(v) => setWorkspacePref({ preventOverlap: v })}
            onSelectPair={(a, b) => {
              selectSpacingPair(a, b);
              if (!showSpacing) setWorkspacePref({ showSpacing: true });
            }}
          />
          <button
            onClick={() => setShowChairs(!showChairs)}
            className={`flex items-center gap-1 px-2 py-1 rounded ${showChairs ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}`}
            data-testid="chairs-toggle"
          >
            <Armchair size={12} />
            Chairs
          </button>
          {tableItems.length > 0 && (
            <button
              onClick={handlePlaceAllChairs}
              className="flex items-center gap-1 px-2 py-1 rounded text-blue-600 hover:bg-blue-50"
              data-testid="place-all-chairs-btn"
            >
              <Armchair size={12} />
              Place All
            </button>
          )}
          {items.some((it) => it.objectType === 'CHAIR') && (
            <button
              onClick={() =>
                openConfirm(
                  'Remove All Chairs',
                  'Remove all chairs from every table?',
                  handleRemoveAllChairs,
                  'Remove',
                )
              }
              className="flex items-center gap-1 px-2 py-1 rounded text-red-600 hover:bg-red-50"
              data-testid="remove-all-chairs-btn"
            >
              <Trash2 size={12} />
              Remove All
            </button>
          )}
          <button
            onClick={() => setShowLabels(!showLabels)}
            className={`flex items-center gap-1 px-2 py-1 rounded ${showLabels ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}`}
            data-testid="labels-toggle"
          >
            <Pencil size={12} />
            Labels
          </button>
          <button
            onClick={() => setShowTableNumbers(!showTableNumbers)}
            className={`flex items-center gap-1 px-2 py-1 rounded ${showTableNumbers ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}`}
            data-testid="table-numbers-toggle"
          >
            <Hash size={12} />
            #Numbers
          </button>
          <button
            onClick={() => setShowIcons(!showIcons)}
            className={`flex items-center gap-1 px-2 py-1 rounded ${showIcons ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}`}
            data-testid="icons-toggle"
          >
            <Palette size={12} />
            Icons
          </button>
          <button
            onClick={() => {
              setShowFamilyColors((v) => !v);
              if (!showChairs && !showFamilyColors) setShowChairs(true);
            }}
            className={`flex items-center gap-1 px-2 py-1 rounded ${showFamilyColors ? 'bg-purple-100 text-purple-700' : 'hover:bg-gray-100'}`}
            data-testid="family-colors-toggle"
            title="Color chairs by family group (requires Chairs view)"
          >
            <Users size={12} />
            Family Colors
          </button>
          {items.some((it) => it.objectType !== 'CHAIR') && (
            <button
              onClick={handleBulkEditOpen}
              className="flex items-center gap-1 px-2 py-1.5 border border-gray-300 rounded text-xs text-gray-700 hover:bg-gray-50"
              title="Bulk edit objects of the same type"
              data-testid="bulk-edit-btn"
            >
              <Layers size={13} />
              Bulk Edit
            </button>
          )}
        </div>
        <div className="flex items-center gap-4">
          <AutoSaveControl
            enabled={floorPlanSave.autoSaveEnabled}
            intervalSec={floorPlanSave.autoSaveIntervalSec}
            onEnabledChange={floorPlanSave.setAutoSaveEnabled}
            onIntervalChange={floorPlanSave.setAutoSaveIntervalSec}
          />
          <span data-testid="utilization">
            Using {roomUtilization.used} of {roomUtilization.total} sq ft ({roomUtilization.pct}%)
          </span>
          <span data-testid="object-count">
            {tableItems.length} tables · {totalSeats} seats | {objectItems.length} objects
            {multiSelect.size > 0 && ` | ${multiSelect.size} selected`}
          </span>
        </div>
      </div>

      {/* ─── Toast ─────────────────────────────────────────────── */}
      {toast && (
        <div
          className={`theme-light fixed bottom-6 left-1/2 -translate-x-1/2 text-white text-sm px-4 py-2 rounded-lg shadow-lg z-50 ${toast.isError ? 'bg-red-600' : 'bg-gray-800'}`}
          data-testid="toast"
        >
          {toast.msg}
        </div>
      )}

      <CeremonyChairsModal
        open={showCeremonyDialog}
        onClose={() => setShowCeremonyDialog(false)}
        onPlace={handlePlaceCeremonyChairs}
      />

      {/* Print Options Modal */}
      {selectedPlanId && (
        <FloorPlanHistoryModal
          open={showHistoryModal}
          onClose={() => setShowHistoryModal(false)}
          floorPlanId={selectedPlanId}
          baseVersion={floorPlanSave.baseVersion()}
          hasUnsavedChanges={floorPlanSave.isDirty}
          onRestored={handleRestored}
        />
      )}
      <FloorPlanPrintModal
        open={showPrintModal}
        onClose={() => setShowPrintModal(false)}
        onPrint={handlePrintPDF}
        showFamilyColorsOption
        onFamilyColorsChange={(checked) => {
          if (checked && !showFamilyColors) {
            setShowFamilyColors(true);
            setShowChairs(true);
          }
        }}
      />

      {/* 3D view */}
      <FloorPlan3DModal
        open={show3DModal || previewDecor !== null}
        onClose={() => {
          setShow3DModal(false);
          setPreviewDecor(null);
        }}
        items={items}
        room={selectedRoom}
        fallbackWidthFt={pxToFt(canvasWidthPx)}
        fallbackHeightFt={pxToFt(canvasHeightPx)}
        chairSides={chairSides as ChairSides}
        planName={loadedPlan?.name}
        decor={previewDecor ?? planDecor}
        stacked={previewDecor !== null}
        onEditLook={
          lookEventId && previewDecor === null
            ? () => {
                setShow3DModal(false);
                setShowLook(true);
              }
            : undefined
        }
      />

      {/* Mounted only while open: it reads the event's pictures. */}
      {lookEventId && showLook && (
        <DecorLookModal
          open
          onClose={() => setShowLook(false)}
          eventId={lookEventId}
          plan={
            loadedPlan
              ? { id: loadedPlan.id, name: loadedPlan.name, decor: loadedPlan.decor }
              : null
          }
          onPreview={setPreviewDecor}
        />
      )}

      {/* Mounted only while open: its queries (renders, the plan's photos) have
          no business running for every producer who opens a floor plan. */}
      {selectedPlanId && showRenderStudio && (
        <RenderStudioModal
          open
          onClose={() => setShowRenderStudio(false)}
          floorPlanId={selectedPlanId}
          items={items}
          room={selectedRoom}
          fallbackWidthFt={pxToFt(canvasWidthPx)}
          fallbackHeightFt={pxToFt(canvasHeightPx)}
          chairSides={chairSides as ChairSides}
          planName={loadedPlan?.name}
          decor={planDecor}
          beforeRender={() =>
            floorPlanSave.isDirty ? floorPlanSave.save('QUIET') : Promise.resolve(true)
          }
        />
      )}

      <BulkEditObjectsModal
        open={showBulkEditModal}
        onClose={() => setShowBulkEditModal(false)}
        items={items}
        defaultChairSizeFt={chairDepthFt}
        onApply={handleBulkEditApply}
      />
    </div>
  );
}
